import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Plus,
  Trash2,
  Video,
  CheckCircle2,
  Circle,
  Activity,
  Clock,
  FileDown,
  FileUp,
} from 'lucide-react';
import { supabase } from '../../services/supabase';
import { useAuthStore } from '../../store/authStore';
import type { Database } from '../../types/database.types';

// ─── Shapes de las queries GYM (Fase 5.6a) ────────────────────────────────────
// Las tablas `gym_*` están en el generado. Los embeds PostgREST anidados no se
// infieren con el cliente sin <Database>, así que el doble cast vía `unknown`
// es necesario por consulta (mismo patrón que la 5.4a).

type GymRoutineRow = Database['public']['Tables']['gym_routines']['Row'];
type GymRoutineExerciseRow = Database['public']['Tables']['gym_routine_exercises']['Row'];
type GymSessionRow = Database['public']['Tables']['gym_sessions']['Row'];
type GymSessionExerciseRow = Database['public']['Tables']['gym_session_exercises']['Row'];
type GymVideoRow = Database['public']['Tables']['gym_videos']['Row'];
type GymExerciseRow = Database['public']['Tables']['gym_exercises']['Row'];

// Embed `gym_routine_exercises(*, gym_exercises(name_es, name_en, category))` —
// usado por `DayDetailPanel` (select más estrecho) y `SessionReviewPanel`. Se
// muestra name_en (con fallback a name_es) — FB-4b.
type RoutineExerciseEmbed = Pick<
  GymRoutineExerciseRow,
  | 'id'
  | 'position'
  | 'block_label'
  | 'sets'
  | 'reps'
  | 'weight_kg'
  | 'time_seconds'
  | 'distance_meters'
  | 'requires_video'
  | 'notes'
> & {
  gym_exercises: Pick<GymExerciseRow, 'name_es' | 'name_en' | 'category'> | null;
};

// Variante del Planner: incluye `gym_exercises.id` para poder reconstruir el
// `exerciseId` del PlanExercise al editar.
type PlannerRoutineExerciseEmbed = Pick<
  GymRoutineExerciseRow,
  | 'id'
  | 'position'
  | 'block_label'
  | 'sets'
  | 'reps'
  | 'weight_kg'
  | 'time_seconds'
  | 'distance_meters'
  | 'requires_video'
  | 'notes'
> & {
  gym_exercises: Pick<GymExerciseRow, 'id' | 'name_es' | 'name_en' | 'category'> | null;
};

// Embed completo `gym_routines + gym_routine_exercises + gym_exercises` para
// `DayDetailPanel`.
type RoutineWithExercises = Pick<GymRoutineRow, 'id' | 'status' | 'session_label' | 'notes'> & {
  gym_routine_exercises: RoutineExerciseEmbed[];
};

// Embed para el Planner: `gym_routines` por semana con sus ejercicios.
type PlannerRoutineRow = Pick<
  GymRoutineRow,
  'id' | 'date' | 'routine_type' | 'session_label' | 'notes' | 'status'
> & {
  gym_routine_exercises: PlannerRoutineExerciseEmbed[];
};

// Embed parcial `gym_session_exercises` usado por `DayDetailPanel`.
type SessionExerciseBrief = Pick<
  GymSessionExerciseRow,
  | 'routine_exercise_id'
  | 'completed'
  | 'actual_sets'
  | 'actual_reps'
  | 'actual_weight_kg'
  | 'actual_time_seconds'
  | 'actual_distance_meters'
  | 'perceived_effort'
  | 'notes'
>;

// Variante para `SessionReviewPanel`: incluye `id` y `completed_at`.
type SessionExerciseForReview = Pick<
  GymSessionExerciseRow,
  | 'id'
  | 'routine_exercise_id'
  | 'completed'
  | 'actual_sets'
  | 'actual_reps'
  | 'actual_weight_kg'
  | 'actual_time_seconds'
  | 'actual_distance_meters'
  | 'perceived_effort'
  | 'notes'
  | 'completed_at'
>;

type SessionWithExercises = Pick<
  GymSessionRow,
  | 'id'
  | 'started_at'
  | 'finished_at'
  | 'status'
  | 'initial_fatigue'
  | 'initial_motivation'
  | 'final_fatigue'
  | 'final_motivation'
  | 'rating'
  | 'comments'
> & {
  gym_session_exercises: SessionExerciseBrief[];
};

type SessionWithExercisesForReview = Pick<
  GymSessionRow,
  | 'id'
  | 'started_at'
  | 'finished_at'
  | 'status'
  | 'initial_fatigue'
  | 'initial_motivation'
  | 'final_fatigue'
  | 'final_motivation'
  | 'rating'
  | 'comments'
> & {
  // §39 (PR-G6) — columnas nuevas para "Marcar revisión completada".
  review_completed_at: string | null;
  review_completed_by: string | null;
  gym_session_exercises: SessionExerciseForReview[];
};

type RoutineBrief = Pick<
  GymRoutineRow,
  'id' | 'notes' | 'session_label' | 'routine_type' | 'status'
>;

type VideoRowBrief = Pick<
  GymVideoRow,
  'id' | 'session_exercise_id' | 'r2_key' | 'feedback_status' | 'feedback_comment' | 'reviewed_at'
>;

// Shape local del array `planned` de `DayDetailPanel` tras el .map de
// transformación (camelCase, valores ya casteados).
interface PlannedExerciseDay {
  id: string;
  name: string;
  category: string;
  blockLabel: string;
  sets: number | null;
  reps: number | null;
  weightKg: number | null;
  durationSeconds: number | null;
  distanceM: number | null;
  requiresVideo: boolean;
}

type TabKey = 'calendario' | 'planificador' | 'revision';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'calendario', label: 'Calendario' },
  { key: 'planificador', label: 'Planificador' },
  { key: 'revision', label: 'Revisión' },
];

const CATEGORY_LABELS: Record<string, string> = {
  movilidad: 'Movilidad',
  activacion: 'Activación',
  isometricos: 'Isométricos',
  pliometricos: 'Pliométricos',
  main_lift: 'Main Lift',
  secondary_lift: 'Secondary Lift',
  auxiliares: 'Auxiliares',
  running: 'Running',
};
const CATEGORY_ORDER = [
  'movilidad',
  'activacion',
  'isometricos',
  'pliometricos',
  'main_lift',
  'secondary_lift',
  'auxiliares',
  'running',
];
const BLOCK_LABELS = ['A', 'B', 'C', 'D'];
const BLOCK_COLORS: Record<string, string> = {
  A: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  B: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  C: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  D: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
};
const DOW_LABELS = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

// Esta pantalla decide si un entrenador planifica encima de una rutina que ya
// existe y si da por hecho que un alumno no ha entrenado. Sus catorce lecturas
// tiraban el `error`, y postgrest-js no rechaza nunca: el fallo llegaba como
// lista vacía o como `null`, que aquí se pinta igual que «no hay nada».
//
// El aviso va SIEMPRE fuera del `x.length > 0 &&` del bloque que acompaña: la
// mitad de estos bloques desaparecen enteros cuando no hay datos, así que un
// aviso dentro no se vería nunca.
function AvisoFallo({ que, testId }: { que: string; testId: string }) {
  return (
    <div className="card border-error/40 bg-error/5 mb-4" data-testid={testId} role="status">
      <p className="text-error text-sm">
        No se ha podido cargar {que}. Lo que salga vacío no significa que no exista: compruébalo
        antes de planificar o de dar la sesión por no hecha.
      </p>
    </div>
  );
}

function pad(n: number) {
  return String(n).padStart(2, '0');
}
function toDateStr(y: number, m: number, d: number) {
  return `${y}-${pad(m + 1)}-${pad(d)}`;
}
// E-3: convierte un timestamptz ISO a la fecha LOCAL del cliente (YYYY-MM-DD).
// El calendario del entrenador razona en fecha local (es UI suya).
function toLocalDateStr(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return toDateStr(d.getFullYear(), d.getMonth(), d.getDate());
}
function formatBonoDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

function getWeekStart(offsetWeeks: number): Date {
  const today = new Date();
  const d = new Date(today);
  d.setDate(today.getDate() - ((today.getDay() + 6) % 7) + offsetWeeks * 7);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getWeekDays(weekStart: Date): string[] {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    return toDateStr(d.getFullYear(), d.getMonth(), d.getDate());
  });
}

function weekRangeLabel(weekStart: Date): string {
  const end = new Date(weekStart);
  end.setDate(weekStart.getDate() + 6);
  const fmt = (d: Date) => d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  return `${fmt(weekStart)} – ${fmt(end)} ${end.getFullYear()}`;
}

// §37 — desde un date string "YYYY-MM-DD" calcula cuántas semanas hay
// entre el lunes de su semana y el lunes de la semana actual. Se usa
// para arrancar el RevisionTab en la semana correcta cuando el monitor
// llega por deep-link desde la vista semanal de Revisión.
function computeWeekOffset(dateStr: string): number {
  const target = new Date(`${dateStr}T12:00:00`);
  const targetMonday = new Date(target);
  targetMonday.setDate(target.getDate() - ((target.getDay() + 6) % 7));
  targetMonday.setHours(0, 0, 0, 0);

  const today = new Date();
  const todayMonday = new Date(today);
  todayMonday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
  todayMonday.setHours(0, 0, 0, 0);

  const ms = targetMonday.getTime() - todayMonday.getTime();
  return Math.round(ms / (1000 * 60 * 60 * 24 * 7));
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function MonitorGymAlumnoPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { profile } = useAuthStore();
  // §37 — deep-link `?tab=revision&date=YYYY-MM-DD` desde la vista semanal
  // de Revisión en MonitorGymPage. Si el query string trae una tab válida,
  // arrancamos en ella; en otro caso, calendario.
  const initialTab = (searchParams.get('tab') as TabKey | null) ?? 'calendario';
  const [tab, setTab] = useState<TabKey>(
    initialTab === 'planificador' || initialTab === 'revision' ? initialTab : 'calendario'
  );
  const initialDate = searchParams.get('date');
  const [userName, setUserName] = useState('');
  // E-3: ventana del bono GYM (activated_at + expiry_date) vía RPC
  // gym_bono_window (SECURITY DEFINER, gate monitor asignado/admin) — la RLS de
  // user_credits no deja al monitor leerlos directamente. Se usa para acotar el
  // rojo del calendario y para mostrar la fecha fin en el detalle.
  const [bonoWindow, setBonoWindow] = useState<{
    activated: string | null;
    expiry: string | null;
  } | null>(null);

  useEffect(() => {
    if (!userId) return;
    supabase
      .from('profiles')
      .select('full_name')
      .eq('id', userId)
      .single()
      .then(({ data, error }) => {
        // El nombre del alumno encabeza la pantalla. Si no se lee, queda en
        // blanco: no se afirma nada, pero se mira el error para no tragárselo.
        if (!error && data) setUserName(data.full_name);
      });
    supabase.rpc('gym_bono_window', { p_user: userId }).then(({ data }) => {
      const row = Array.isArray(data) ? data[0] : null;
      setBonoWindow({ activated: row?.activated_at ?? null, expiry: row?.expiry_date ?? null });
    });
  }, [userId]);

  // Cuando el usuario navega entre tabs manualmente, limpiamos los
  // query params (deep-link) para que ya no afecten al estado interno.
  const handleTabChange = (key: TabKey) => {
    setTab(key);
    if (searchParams.has('tab') || searchParams.has('date')) {
      searchParams.delete('tab');
      searchParams.delete('date');
      setSearchParams(searchParams, { replace: true });
    }
  };

  if (!userId || !profile) return null;

  return (
    <div className="space-y-6">
      <div>
        <button
          onClick={() => navigate('/monitor/gym')}
          className="flex items-center gap-2 text-[#A3E635] font-inter text-sm mb-4 hover:opacity-80 transition-opacity"
        >
          <ArrowLeft size={16} /> Volver a Mis alumnos
        </button>
        <h2 className="font-oswald text-2xl text-white tracking-wide">{userName}</h2>
        {bonoWindow?.expiry && (
          <p className="font-inter text-sm text-text-secondary mt-1" data-testid="gym-bono-expiry">
            Bono hasta el <span className="text-white">{formatBonoDate(bonoWindow.expiry)}</span>
          </p>
        )}
      </div>

      <div className="flex gap-1 bg-bg-surface border border-bg-border rounded-xl p-1 w-fit">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`gym-tab-${t.key}`}
            onClick={() => handleTabChange(t.key)}
            className={`px-5 py-2 rounded-lg text-sm font-inter transition-colors ${
              tab === t.key
                ? 'bg-[#A3E635] text-bg-primary font-semibold'
                : 'text-text-secondary hover:text-white'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'calendario' && (
        <CalendarTab
          userId={userId}
          userName={userName}
          windowStart={toLocalDateStr(bonoWindow?.activated)}
          windowEnd={toLocalDateStr(bonoWindow?.expiry)}
        />
      )}
      {tab === 'planificador' && <PlannerTab userId={userId} monitorId={profile.id} />}
      {tab === 'revision' && (
        <RevisionTab userId={userId} monitorId={profile.id} initialDate={initialDate} />
      )}
    </div>
  );
}

// ─── CALENDARIO ───────────────────────────────────────────────────────────────

function CalendarTab({
  userId,
  userName,
  windowStart,
  windowEnd,
}: {
  userId: string;
  userName: string;
  windowStart: string | null;
  windowEnd: string | null;
}) {
  const today = new Date();
  const [current, setCurrent] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [statuses, setStatuses] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [mesIlegible, setMesIlegible] = useState(false);
  const [dayDetail, setDayDetail] = useState<string | null>(null);

  const loadMonth = useCallback(
    async (monthStart: Date) => {
      setLoading(true);
      const y = monthStart.getFullYear(),
        m = monthStart.getMonth();
      const daysInMonth = new Date(y, m + 1, 0).getDate();

      // E-2: el color del calendario del entrenador es de PLANIFICACIÓN, no de
      // ejecución. Verde = ese día tiene rutina PUBLICADA (cualquier
      // routine_type, incl. 'descanso'); rojo = no la tiene. Un borrador NO
      // cuenta (queda rojo). Una sola query al rango del mes filtrando
      // status='published' (la RLS de gym_routines deja al monitor asignado
      // leer las rutinas de su alumno). El seguimiento de EJECUCIÓN vive en la
      // pestaña "Revisión" y sigue usando gym_routine_status_for_day.
      const { data, error } = await supabase
        .from('gym_routines')
        .select('date')
        .eq('user_id', userId)
        .eq('status', 'published')
        .gte('date', toDateStr(y, m, 1))
        .lte('date', toDateStr(y, m, daysInMonth));

      // ⚠️ Sin esta lectura el mes entero sale ROJO, que en este calendario
      //    significa «ese día no tiene rutina publicada». El entrenador lo
      //    mira para saber qué le falta planificar: un rojo falso le hace
      //    planificar encima de lo que ya había.
      setMesIlegible(!!error);
      if (error) {
        setLoading(false);
        return;
      }

      const map: Record<string, string> = {};
      (data ?? []).forEach((r: { date: string }) => {
        map[r.date] = 'published';
      });
      setStatuses(map);
      setLoading(false);
    },
    [userId]
  );

  useEffect(() => {
    loadMonth(current);
  }, [current, loadMonth]);

  const y = current.getFullYear(),
    m = current.getMonth();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const firstDow = (new Date(y, m, 1).getDay() + 6) % 7;
  const cells = Array.from({ length: Math.ceil((firstDow + daysInMonth) / 7) * 7 }, (_, i) => {
    const day = i - firstDow + 1;
    return day >= 1 && day <= daysInMonth ? day : null;
  });
  const monthName = current.toLocaleString('es-ES', { month: 'long', year: 'numeric' });
  const todayStr = toDateStr(today.getFullYear(), today.getMonth(), today.getDate());

  // E-3: horizonte del ROJO (fecha local, semana lunes→domingo). El rojo cubre
  // hasta el domingo de ESTA semana; si hoy es viernes/sábado/domingo, se abre
  // además toda la semana SIGUIENTE (hasta el domingo próximo). Los días dentro
  // de la ventana del bono más allá de ese horizonte quedan NEUTROS.
  const dow = today.getDay(); // 0=domingo … 6=sábado
  const daysToSunday = (7 - dow) % 7;
  const opensNextWeek = dow === 5 || dow === 6 || dow === 0; // vi/sá/do
  const horizon = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  horizon.setDate(horizon.getDate() + daysToSunday + (opensNextWeek ? 7 : 0));
  const redHorizonStr = toDateStr(horizon.getFullYear(), horizon.getMonth(), horizon.getDate());

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_252px] gap-6 items-start">
      {mesIlegible && <AvisoFallo que="las rutinas del mes" testId="gym-mes-ilegible" />}
      {/* ── LEFT: day detail (cuando hay día seleccionado) o resumen semanal
          (§34 PR-G4: antes era un placeholder mudo "Selecciona un día"; ahora
          muestra los 7 días de esta semana con label de sesión + estado para
          que el trainer no tenga que clickar día a día). ── */}
      <div>
        {dayDetail ? (
          <DayDetailPanel
            userId={userId}
            userName={userName}
            date={dayDetail}
            onClose={() => setDayDetail(null)}
          />
        ) : (
          <WeekSummaryPanel userId={userId} onSelectDay={setDayDetail} />
        )}
      </div>

      {/* ── RIGHT: compact mini-calendar ── */}
      <div className="bg-bg-surface border border-bg-border rounded-xl p-4 space-y-2 shrink-0">
        {/* Month header */}
        <div className="flex items-center justify-between mb-1">
          <button
            onClick={() => {
              setCurrent(new Date(y, m - 1, 1));
              setDayDetail(null);
            }}
            className="w-7 h-7 rounded flex items-center justify-center hover:bg-bg-elevated transition-colors"
          >
            <ChevronLeft size={13} className="text-text-secondary" />
          </button>
          <h3 className="font-oswald text-sm text-white capitalize">{monthName}</h3>
          <button
            onClick={() => {
              setCurrent(new Date(y, m + 1, 1));
              setDayDetail(null);
            }}
            className="w-7 h-7 rounded flex items-center justify-center hover:bg-bg-elevated transition-colors"
          >
            <ChevronRight size={13} className="text-text-secondary" />
          </button>
        </div>

        {/* DOW header */}
        <div className="grid grid-cols-7 text-center">
          {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map((d) => (
            <span key={d} className="font-inter text-[10px] text-text-secondary pb-1">
              {d}
            </span>
          ))}
        </div>

        {/* Day cells */}
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <span className="font-oswald text-xs text-[#A3E635] animate-pulse">Cargando...</span>
          </div>
        ) : (
          <div className="grid grid-cols-7 gap-0.5">
            {cells.map((day, i) => {
              if (!day) return <div key={`e${i}`} />;
              const dateStr = toDateStr(y, m, day);
              const status = statuses[dateStr] ?? 'none';
              // E-3: color por PLANIFICACIÓN acotado a la ventana del bono.
              //  · VERDE  = rutina publicada ese día (cualquier tipo).
              //  · ROJO   = sin publicada, DENTRO de la ventana [inicio,fin] del
              //             bono y hasta el horizonte (domingo de esta semana, o
              //             de la siguiente si hoy es vi/sá/do).
              //  · NEUTRO = fuera de la ventana del bono, o dentro pero más allá
              //             del horizonte (futuro que aún no entra).
              const inWindow =
                !!windowStart && !!windowEnd && dateStr >= windowStart && dateStr <= windowEnd;
              const dotColor =
                status === 'published'
                  ? '#22C55E'
                  : inWindow && dateStr <= redHorizonStr
                    ? '#EF4444'
                    : null;
              const isToday = dateStr === todayStr;
              const isSelected = dayDetail === dateStr;
              // Dot 2×2 para que el trainer distinga el estado de un vistazo.
              return (
                <button
                  key={dateStr}
                  data-testid={`gym-cal-day-${dateStr}`}
                  data-status={status}
                  onClick={() => setDayDetail(isSelected ? null : dateStr)}
                  className={`relative flex flex-col items-center justify-center h-8 rounded transition-colors ${
                    isSelected
                      ? 'bg-[#A3E635]'
                      : isToday
                        ? 'border border-[#A3E635]/60'
                        : 'hover:bg-bg-elevated'
                  }`}
                >
                  <span
                    className={`font-inter text-xs leading-none ${
                      isSelected
                        ? 'text-[#0A0A0A] font-bold'
                        : isToday
                          ? 'text-[#A3E635] font-semibold'
                          : 'text-white'
                    }`}
                  >
                    {day}
                  </span>
                  {dotColor && !isSelected && (
                    <span
                      className="absolute bottom-0.5 w-2 h-2 rounded-full"
                      style={{ backgroundColor: dotColor }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Legend */}
        <div className="flex flex-col gap-1 pt-2 border-t border-bg-border">
          {[
            { color: '#22C55E', label: 'Planificado' },
            { color: '#EF4444', label: 'Sin planificar' },
          ].map(({ color, label }) => (
            <div key={label} className="flex items-center gap-1.5">
              <span
                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: color }}
              />
              <span className="font-inter text-[11px] text-text-secondary">{label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── §34 (PR-G4): resumen semanal para el trainer ──────────────────────────
// Lista los 7 días de la semana en curso con el label de sesión + número de
// ejercicios planificados + un pill de estado. Click → abre DayDetailPanel.
//
// Estado por día:
//   - 'green'           → completado
//   - 'orange'          → iniciado / parcial
//   - 'red' + pasado    → no realizado
//   - 'red' + hoy/futuro→ planificado pendiente
//   - 'rest'            → descanso
//   - 'none'            → sin rutina publicada
//
// Datos: dos queries — `gym_routines` con embed de ejercicios (label + count)
// y `gym_routine_status_for_day` por día (RPC ya existente). 7+7 = 14 calls,
// despreciable.
type WeekSummaryDay = {
  date: string;
  weekdayLabel: string; // 'Lunes', 'Martes', ...
  sessionLabel: string | null;
  exerciseCount: number;
  routinePublished: boolean;
  status: 'green' | 'orange' | 'red' | 'rest' | 'none';
};

type WeekSummaryRoutine = Pick<GymRoutineRow, 'date' | 'session_label' | 'status'> & {
  gym_routine_exercises: { id: string }[];
};

function WeekSummaryPanel({
  userId,
  onSelectDay,
}: {
  userId: string;
  onSelectDay: (date: string) => void;
}) {
  const [days, setDays] = useState<WeekSummaryDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [semanaIlegible, setSemanaIlegible] = useState(false);

  useEffect(() => {
    async function load() {
      setLoading(true);
      const today = new Date();
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - ((today.getDay() + 6) % 7));
      weekStart.setHours(0, 0, 0, 0);

      const dates: string[] = Array.from({ length: 7 }, (_, i) => {
        const d = new Date(weekStart);
        d.setDate(weekStart.getDate() + i);
        return toDateStr(d.getFullYear(), d.getMonth(), d.getDate());
      });

      const { data: routinesRaw, error: errRoutines } = await supabase
        .from('gym_routines')
        .select('date, session_label, status, gym_routine_exercises(id)')
        .eq('user_id', userId)
        .in('date', dates);
      setSemanaIlegible(!!errRoutines);
      if (errRoutines) {
        setLoading(false);
        return;
      }
      const routines = (routinesRaw ?? []) as unknown as WeekSummaryRoutine[];
      const routineMap = new Map<string, WeekSummaryRoutine>();
      routines.forEach((r) => routineMap.set(r.date, r));

      // 'none' es el valor que significa «ese día no hay nada». Si la RPC
      // falla se devolvía exactamente eso, así que el fallo se leía como
      // respuesta. `desconocido` lo separa.
      const statusResults = await Promise.all(
        dates.map(async (date) => {
          const { data, error } = await supabase.rpc('gym_routine_status_for_day', {
            p_user_id: userId,
            p_date: date,
          });
          if (error) return [date, 'desconocido'] as const;
          const s = typeof data === 'string' ? data : 'none';
          return [date, s] as const;
        })
      );
      // `desconocido` acaba estrechándose a 'none' y pintando '—', que no
      // afirma nada. Pero un '—' sin explicación se lee como «ese día no hay
      // nada planificado», así que si alguno falló, se dice.
      if (statusResults.some(([, st]) => st === 'desconocido')) setSemanaIlegible(true);

      const statusMap = new Map(statusResults);

      const built: WeekSummaryDay[] = dates.map((date) => {
        const r = routineMap.get(date);
        const rawStatus = statusMap.get(date) ?? 'none';
        // El status 'red' lo recibe tanto un día pasado sin ejecutar como un
        // día futuro con rutina ya publicada. Distinguimos los dos casos en
        // `buildWeekSummaryBadge` comparando contra `todayStr`. Aquí
        // simplemente clasificamos el raw al union conocido.
        const status: WeekSummaryDay['status'] =
          rawStatus === 'green' ||
          rawStatus === 'orange' ||
          rawStatus === 'red' ||
          rawStatus === 'rest'
            ? (rawStatus as WeekSummaryDay['status'])
            : 'none';
        const weekdayLabel = new Date(`${date}T12:00:00`).toLocaleDateString('es-ES', {
          weekday: 'long',
        });
        return {
          date,
          weekdayLabel: weekdayLabel.charAt(0).toUpperCase() + weekdayLabel.slice(1),
          sessionLabel: r?.session_label ?? null,
          exerciseCount: r?.gym_routine_exercises?.length ?? 0,
          routinePublished: r?.status === 'published',
          status,
        };
      });
      setDays(built);
      setLoading(false);
    }
    load();
  }, [userId]);

  const today = new Date();
  const todayStr = toDateStr(today.getFullYear(), today.getMonth(), today.getDate());

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 bg-bg-surface border border-bg-border rounded-xl">
        <span className="font-oswald text-[#A3E635] animate-pulse">Cargando…</span>
      </div>
    );
  }

  return (
    <div
      className="bg-bg-surface border border-bg-border rounded-xl p-4 space-y-3"
      data-testid="gym-week-summary"
    >
      {semanaIlegible && <AvisoFallo que="el resumen de la semana" testId="gym-semana-ilegible" />}
      <div className="flex items-center justify-between">
        <h3 className="font-oswald text-base uppercase tracking-wide text-white">
          Resumen de esta semana
        </h3>
        <span className="font-inter text-[11px] text-text-secondary">
          {days.length > 0 && `${days[0].date.slice(8, 10)} – ${days[6].date.slice(8, 10)}`}
        </span>
      </div>
      <div className="flex flex-col divide-y divide-bg-border">
        {days.map((d) => {
          const isToday = d.date === todayStr;
          const isPast = d.date < todayStr;
          const hasRoutine = d.exerciseCount > 0 || d.status === 'rest';
          const badge = buildWeekSummaryBadge(d.status, isPast);
          return (
            <button
              key={d.date}
              data-testid={`gym-week-day-${d.date}`}
              data-status={d.status}
              onClick={() => onSelectDay(d.date)}
              className={`flex items-center gap-3 py-2.5 text-left hover:bg-bg-elevated/40 transition-colors rounded-lg px-2 -mx-2 ${
                isToday ? 'bg-[#A3E635]/5' : ''
              }`}
            >
              <div className="flex flex-col items-start w-20 flex-shrink-0">
                <span
                  className={`font-oswald text-sm uppercase tracking-wider ${
                    isToday ? 'text-[#A3E635]' : 'text-white'
                  }`}
                >
                  {d.weekdayLabel.slice(0, 3)}
                </span>
                <span className="font-inter text-[11px] text-text-secondary">
                  {d.date.slice(8, 10)}/{d.date.slice(5, 7)}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                {hasRoutine ? (
                  <>
                    <p className="font-inter text-sm text-white truncate">
                      {d.sessionLabel || (d.status === 'rest' ? 'Descanso' : 'Rutina sin nombre')}
                    </p>
                    {d.status !== 'rest' && (
                      <p className="font-inter text-xs text-text-secondary">
                        {d.exerciseCount} ejercicio{d.exerciseCount === 1 ? '' : 's'}
                        {!d.routinePublished && ' · borrador'}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="font-inter text-xs text-text-secondary italic">Sin rutina</p>
                )}
              </div>
              <span
                className={`text-[11px] font-inter font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${badge.className}`}
              >
                {badge.label}
              </span>
            </button>
          );
        })}
      </div>
      <p className="font-inter text-[11px] text-text-secondary italic">
        Haz clic en un día para ver el detalle.
      </p>
    </div>
  );
}

function buildWeekSummaryBadge(
  status: WeekSummaryDay['status'],
  isPast: boolean
): { label: string; className: string } {
  switch (status) {
    case 'green':
      return {
        label: 'Completada',
        className: 'text-[#22C55E] bg-[#22C55E]/10 border-[#22C55E]/30',
      };
    case 'orange':
      return {
        label: 'Iniciada',
        className: 'text-[#F59E0B] bg-[#F59E0B]/10 border-[#F59E0B]/30',
      };
    case 'red':
      return isPast
        ? { label: 'No realizada', className: 'text-[#EF4444] bg-[#EF4444]/10 border-[#EF4444]/30' }
        : {
            label: 'Planificada',
            className: 'text-[#60A5FA] bg-[#60A5FA]/10 border-[#60A5FA]/30',
          };
    case 'rest':
      return {
        label: 'Descanso',
        className: 'text-text-secondary bg-bg-border/40 border-bg-border',
      };
    case 'none':
    default:
      return {
        label: '—',
        className: 'text-text-secondary bg-transparent border-bg-border',
      };
  }
}

function DayDetailPanel({
  userId,
  date,
  onClose,
}: {
  userId: string;
  userName?: string;
  date: string;
  onClose: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [diaIlegible, setDiaIlegible] = useState(false);
  const [sesionIlegible, setSesionIlegible] = useState(false);
  const [routineStatus, setRoutineStatus] = useState('');
  const [routineNotes, setRoutineNotes] = useState('');
  const [sessionLabel, setSessionLabel] = useState('');
  const [planned, setPlanned] = useState<PlannedExerciseDay[]>([]);
  const [session, setSession] = useState<SessionWithExercises | null>(null);
  const [completed, setCompleted] = useState<Record<string, SessionExerciseBrief>>({});

  useEffect(() => {
    async function load() {
      setLoading(true);
      const { data: routineRaw, error: errRoutine } = await supabase
        .from('gym_routines')
        .select(
          'id, status, session_label, notes, gym_routine_exercises(id, position, block_label, sets, reps, weight_kg, time_seconds, distance_meters, requires_video, notes, gym_exercises(name_es, name_en, category))'
        )
        .eq('user_id', userId)
        .eq('date', date)
        .maybeSingle();
      setDiaIlegible(!!errRoutine);
      if (errRoutine) {
        setLoading(false);
        return;
      }

      const routine = routineRaw as unknown as RoutineWithExercises | null;
      if (routine) {
        setRoutineStatus(routine.status ?? '');
        setSessionLabel(routine.session_label ?? '');
        setRoutineNotes(routine.notes ?? '');
        const exs: PlannedExerciseDay[] = (routine.gym_routine_exercises ?? [])
          .slice()
          .sort((a, b) => a.position - b.position)
          .map((e) => ({
            id: e.id,
            name: e.gym_exercises?.name_en || e.gym_exercises?.name_es || 'Ejercicio',
            category: e.gym_exercises?.category ?? '',
            blockLabel: e.block_label ?? '',
            sets: e.sets,
            reps: e.reps,
            weightKg: e.weight_kg,
            durationSeconds: e.time_seconds,
            distanceM: e.distance_meters,
            requiresVideo: e.requires_video,
          }));
        setPlanned(exs);

        const { data: sessRaw, error: errSess } = await supabase
          .from('gym_sessions')
          .select(
            'id, started_at, finished_at, status, initial_fatigue, initial_motivation, final_fatigue, final_motivation, rating, comments, gym_session_exercises(routine_exercise_id, completed, actual_sets, actual_reps, actual_weight_kg, actual_time_seconds, actual_distance_meters, perceived_effort, notes)'
          )
          .eq('routine_id', routine.id)
          .eq('user_id', userId)
          .maybeSingle();

        // ⚠️ Esta es la que dice si el alumno HIZO la sesión. Sin ella se
        //    pinta igual que si no la hubiera hecho.
        setSesionIlegible(!!errSess);

        const sess = sessRaw as unknown as SessionWithExercises | null;
        if (sess) {
          setSession(sess);
          const map: Record<string, SessionExerciseBrief> = {};
          (sess.gym_session_exercises ?? []).forEach((se) => {
            map[se.routine_exercise_id] = se;
          });
          setCompleted(map);
        }
      }
      setLoading(false);
    }
    load();
  }, [userId, date]);

  const formattedDate = new Date(`${date}T12:00:00`).toLocaleDateString('es-ES', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  const SESSION_STATUS_LABELS: Record<string, string> = {
    in_progress: 'En progreso',
    completed: 'Completado',
    partial: 'Parcial',
    abandoned: 'Abandonado',
  };
  const SESSION_STATUS_COLORS: Record<string, string> = {
    in_progress: '#F59E0B',
    completed: '#22C55E',
    partial: '#F59E0B',
    abandoned: '#EF4444',
  };

  const doneCount = Object.values(completed).filter((c) => c.completed).length;

  // Group exercises by category
  const grouped = CATEGORY_ORDER.map((cat) => ({
    cat,
    exs: planned.filter((e) => e.category === cat),
  })).filter((g) => g.exs.length > 0);

  return (
    <div className="card border-[#A3E635]/20 bg-[#A3E635]/5 space-y-4">
      {diaIlegible && <AvisoFallo que="la rutina de este día" testId="gym-dia-ilegible" />}
      {sesionIlegible && <AvisoFallo que="la ejecución del alumno" testId="gym-sesion-ilegible" />}
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <p className="font-oswald text-white text-base capitalize">{formattedDate}</p>
          <div className="flex items-center gap-2 flex-wrap">
            {sessionLabel && (
              <span className="text-xs font-oswald font-semibold uppercase tracking-wide px-2 py-0.5 rounded bg-[#A3E635]/20 border border-[#A3E635]/40 text-[#A3E635]">
                {sessionLabel}
              </span>
            )}
            {routineStatus === 'published' && (
              <span className="text-xs font-inter text-[#22C55E] bg-[#22C55E]/10 border border-[#22C55E]/30 px-2 py-0.5 rounded-full">
                Planificado
              </span>
            )}
            {routineStatus === 'draft' && (
              <span className="text-xs font-inter text-[#F59E0B] bg-[#F59E0B]/10 border border-[#F59E0B]/30 px-2 py-0.5 rounded-full">
                Borrador
              </span>
            )}
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-text-secondary hover:text-white text-xl leading-none"
        >
          ✕
        </button>
      </div>

      {loading ? (
        <div className="text-[#A3E635] font-oswald text-sm animate-pulse">Cargando...</div>
      ) : planned.length === 0 ? (
        <p className="font-inter text-text-secondary text-sm">
          Sin rutina planificada para este día
        </p>
      ) : (
        <>
          {routineNotes && (
            <p className="font-inter text-text-secondary text-sm italic border-l-2 border-[#A3E635]/40 pl-2">
              {routineNotes}
            </p>
          )}

          {session && (
            <div className="bg-bg-surface border border-bg-border rounded-lg p-3 space-y-2">
              <div className="flex items-center gap-2">
                <Activity
                  size={14}
                  style={{ color: SESSION_STATUS_COLORS[session.status] ?? '#A0A0A0' }}
                />
                <span
                  className="font-oswald text-sm"
                  style={{ color: SESSION_STATUS_COLORS[session.status] ?? '#A0A0A0' }}
                >
                  {SESSION_STATUS_LABELS[session.status] ?? session.status}
                </span>
                <span className="font-inter text-text-secondary text-xs ml-auto">
                  {new Date(session.started_at).toLocaleTimeString('es-ES', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                  {session.finished_at &&
                    ` – ${new Date(session.finished_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-bg-border rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#22C55E] rounded-full"
                    style={{ width: `${Math.round((doneCount / planned.length) * 100)}%` }}
                  />
                </div>
                <span className="font-inter text-xs text-text-secondary">
                  {doneCount}/{planned.length}
                </span>
              </div>
              <div className="flex flex-wrap gap-4">
                {session.initial_fatigue != null && (
                  <Metric label="Fatiga ini." value={`${session.initial_fatigue}/10`} />
                )}
                {session.initial_motivation != null && (
                  <Metric label="Motivación" value={`${session.initial_motivation}/10`} />
                )}
                {session.final_fatigue != null && (
                  <Metric label="Fatiga fin." value={`${session.final_fatigue}/10`} />
                )}
                {session.rating != null && (
                  <Metric label="Valoración" value={'⭐'.repeat(session.rating)} />
                )}
              </div>
              {session.comments && (
                <p className="font-inter text-xs text-text-secondary italic border-l-2 border-bg-border pl-2">
                  {session.comments}
                </p>
              )}
            </div>
          )}

          {!session && (
            <div className="flex items-center gap-2 text-text-secondary">
              <Clock size={14} />
              <span className="font-inter text-xs">El alumno aún no ha realizado esta sesión</span>
            </div>
          )}

          {/* Exercises grouped by category */}
          <div className="space-y-3">
            {grouped.map(({ cat, exs }) => (
              <div key={cat}>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="font-oswald text-xs text-text-secondary tracking-widest uppercase">
                    {CATEGORY_LABELS[cat] ?? cat}
                  </span>
                  <div className="flex-1 h-px bg-bg-border" />
                  <span className="font-inter text-xs text-text-secondary">{exs.length}</span>
                </div>
                <div className="space-y-1">
                  {exs.map((ex) => {
                    const done = completed[ex.id];
                    const isDone = done?.completed === true;
                    return (
                      <div key={ex.id} className="flex items-start gap-2 py-1">
                        {done ? (
                          isDone ? (
                            <CheckCircle2 size={14} className="text-[#22C55E] mt-0.5 shrink-0" />
                          ) : (
                            <Circle size={14} className="text-[#EF4444] mt-0.5 shrink-0" />
                          )
                        ) : (
                          <Circle size={14} className="text-text-secondary mt-0.5 shrink-0" />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {ex.blockLabel && (
                              <span
                                className={`text-xs font-semibold font-inter px-1.5 py-0.5 rounded border ${BLOCK_COLORS[ex.blockLabel] ?? 'text-text-secondary border-bg-border'}`}
                              >
                                {ex.blockLabel}
                              </span>
                            )}
                            <span
                              className={`font-inter text-sm ${isDone ? 'text-[#22C55E]' : 'text-white'}`}
                            >
                              {ex.name}
                            </span>
                            {ex.requiresVideo && (
                              <span className="text-xs font-inter text-[#A3E635] bg-[#A3E635]/10 border border-[#A3E635]/30 px-1.5 py-0.5 rounded shrink-0">
                                📹
                              </span>
                            )}
                          </div>
                          <p className="font-inter text-xs text-text-secondary">
                            {buildPlannedSummary(ex)}
                            {done && isDone && (
                              <span className="text-[#22C55E]"> → {buildActualSummary(done)}</span>
                            )}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── PLANIFICADOR ─────────────────────────────────────────────────────────────

interface PlanExercise {
  id?: string;
  exerciseId: string;
  exerciseName: string;
  exerciseCategory: string;
  blockLabel: string;
  sets: string;
  reps: string;
  weightKg: string;
  durationSeconds: string;
  distanceM: string;
  notes: string;
  requiresVideo: boolean;
}

interface PlanDay {
  date: string;
  routineId?: string;
  sessionLabel: string;
  notes: string;
  status: string;
  // FB-9: routine_type del día. 'descanso' (publicado, 0 ejercicios) cuenta
  // como planificación válida; el resto de tipos llevan ejercicios.
  routineType?: string;
  exercises: PlanExercise[];
}

function PlannerTab({ userId, monitorId }: { userId: string; monitorId: string }) {
  const today = new Date();
  const todayStr = toDateStr(today.getFullYear(), today.getMonth(), today.getDate());
  const [weekOffset, setWeekOffset] = useState(0);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [planDays, setPlanDays] = useState<Record<string, PlanDay>>({});
  const [loading, setLoading] = useState(true);
  const [semanaPlanIlegible, setSemanaPlanIlegible] = useState(false);

  const weekStart = getWeekStart(weekOffset);
  const weekDays = getWeekDays(weekStart);

  const loadWeek = useCallback(
    async (days: string[]) => {
      setLoading(true);
      const { data, error } = await supabase
        .from('gym_routines')
        .select(
          'id, date, routine_type, session_label, notes, status, gym_routine_exercises(id, position, sets, reps, weight_kg, time_seconds, distance_meters, block_label, notes, requires_video, gym_exercises(id, name_es, name_en, category))'
        )
        .eq('user_id', userId)
        .in('date', days);

      // ⚠️ El planificador con la semana vacía invita a crear rutinas que ya
      //    existen. Y crear encima de una publicada no es inocuo.
      setSemanaPlanIlegible(!!error);
      if (error) {
        setLoading(false);
        return;
      }

      const rows = (data ?? []) as unknown as PlannerRoutineRow[];
      const map: Record<string, PlanDay> = {};
      rows.forEach((r) => {
        map[r.date] = {
          date: r.date,
          routineId: r.id,
          sessionLabel: r.session_label ?? '',
          notes: r.notes ?? '',
          status: r.status,
          routineType: r.routine_type,
          exercises: (r.gym_routine_exercises ?? [])
            .slice()
            .sort((a, b) => a.position - b.position)
            .map((e) => ({
              id: e.id,
              exerciseId: e.gym_exercises?.id ?? '',
              exerciseName: e.gym_exercises?.name_en || e.gym_exercises?.name_es || '',
              exerciseCategory: e.gym_exercises?.category ?? '',
              blockLabel: e.block_label ?? '',
              sets: String(e.sets ?? ''),
              reps: String(e.reps ?? ''),
              weightKg: String(e.weight_kg ?? ''),
              durationSeconds: String(e.time_seconds ?? ''),
              distanceM: String(e.distance_meters ?? ''),
              notes: e.notes ?? '',
              requiresVideo: e.requires_video,
            })),
        };
      });
      days.forEach((d) => {
        if (!map[d])
          map[d] = {
            date: d,
            sessionLabel: '',
            notes: '',
            status: 'draft',
            routineType: undefined,
            exercises: [],
          };
      });
      setPlanDays(map);
      setLoading(false);
    },
    [userId]
  );

  useEffect(() => {
    loadWeek(weekDays);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekOffset, userId]);

  function updateDay(date: string, updates: Partial<PlanDay>) {
    setPlanDays((p) => ({ ...p, [date]: { ...p[date], ...updates } }));
  }

  return (
    <div className="space-y-4">
      {semanaPlanIlegible && (
        <AvisoFallo que="las rutinas de esta semana" testId="gym-planner-ilegible" />
      )}
      {/* Week navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => {
            setWeekOffset((w) => w - 1);
            setSelectedDate(null);
          }}
          className="w-9 h-9 rounded-lg bg-bg-surface border border-bg-border flex items-center justify-center hover:border-[#A3E635]/40 transition-colors"
        >
          <ChevronLeft size={16} className="text-text-secondary" />
        </button>
        <span className="font-inter text-sm text-white capitalize">
          {weekRangeLabel(weekStart)}
        </span>
        <button
          onClick={() => {
            setWeekOffset((w) => w + 1);
            setSelectedDate(null);
          }}
          className="w-9 h-9 rounded-lg bg-bg-surface border border-bg-border flex items-center justify-center hover:border-[#A3E635]/40 transition-colors"
        >
          <ChevronRight size={16} className="text-text-secondary" />
        </button>
      </div>

      {/* Week row */}
      {loading ? (
        <div className="flex items-center justify-center h-20">
          <div className="text-[#A3E635] font-oswald animate-pulse">Cargando...</div>
        </div>
      ) : (
        <div className="grid grid-cols-7 gap-1">
          {weekDays.map((date, i) => {
            const day = planDays[date];
            const isToday = date === todayStr;
            const isSelected = selectedDate === date;
            const hasExercises = (day?.exercises.length ?? 0) > 0;
            const isPublished = day?.status === 'published';
            // FB-9: descanso (publicado, 0 ejercicios) → punto neutro.
            const isRest = day?.routineType === 'descanso' && !hasExercises;
            return (
              <button
                key={date}
                data-testid={`gym-planner-day-${date}`}
                data-has-exercises={hasExercises ? '1' : '0'}
                data-published={isPublished ? '1' : '0'}
                onClick={() => setSelectedDate(isSelected ? null : date)}
                className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border transition-colors ${
                  isSelected
                    ? 'bg-[#A3E635]/10 border-[#A3E635]/50'
                    : isToday
                      ? 'border-[#A3E635]/40'
                      : 'border-bg-border hover:bg-bg-surface'
                }`}
              >
                <span className="font-inter text-xs text-text-secondary">{DOW_LABELS[i]}</span>
                <span
                  className={`font-inter text-sm font-medium ${isToday ? 'text-[#A3E635]' : 'text-white'}`}
                >
                  {new Date(`${date}T12:00:00`).getDate()}
                </span>
                <div
                  className="w-2 h-2 rounded-full"
                  style={{
                    backgroundColor: hasExercises
                      ? isPublished
                        ? '#22C55E'
                        : '#F59E0B'
                      : isRest
                        ? '#6B7280'
                        : 'transparent',
                  }}
                />
              </button>
            );
          })}
        </div>
      )}

      {/* §35 (PR-G4): visión semanal con label y nº de ejercicios por día sin
          requerir click. Antes el trainer veía sólo 7 pastillas con un dot;
          ahora la lista lateral expone lo planificado al primer vistazo. */}
      {!loading && (
        <PlannerWeekOverview
          weekDays={weekDays}
          planDays={planDays}
          todayStr={todayStr}
          selectedDate={selectedDate}
          onSelectDay={(d) => setSelectedDate(d === selectedDate ? null : d)}
        />
      )}

      {/* Day editor */}
      {selectedDate && planDays[selectedDate] && (
        <PlannerDayPanel
          key={selectedDate}
          date={selectedDate}
          day={planDays[selectedDate]}
          userId={userId}
          monitorId={monitorId}
          onChange={(updates) => updateDay(selectedDate, updates)}
          onSaved={() => loadWeek(weekDays)}
        />
      )}
    </div>
  );
}

// ─── §35 (PR-G4): resumen semanal en el Planificador ───────────────────────
// Lista los 7 días con label de sesión, número de ejercicios y estado
// publicado/borrador. Click en una fila selecciona el día (equivalente al
// click en la pastilla).
function PlannerWeekOverview({
  weekDays,
  planDays,
  todayStr,
  selectedDate,
  onSelectDay,
}: {
  weekDays: string[];
  planDays: Record<string, PlanDay>;
  todayStr: string;
  selectedDate: string | null;
  onSelectDay: (date: string) => void;
}) {
  return (
    <div
      className="bg-bg-surface border border-bg-border rounded-xl divide-y divide-bg-border"
      data-testid="gym-planner-week-overview"
    >
      {weekDays.map((date, i) => {
        const day = planDays[date];
        const exerciseCount = day?.exercises.length ?? 0;
        const isPublished = day?.status === 'published';
        // FB-9: el descanso (publicado, 0 ejercicios) es plan válido.
        const isRest = day?.routineType === 'descanso' && exerciseCount === 0;
        const isToday = date === todayStr;
        const isSelected = selectedDate === date;
        const weekdayLabel = new Date(`${date}T12:00:00`).toLocaleDateString('es-ES', {
          weekday: 'long',
        });
        const dayNum = new Date(`${date}T12:00:00`).getDate();
        const monthShort = new Date(`${date}T12:00:00`).toLocaleDateString('es-ES', {
          month: 'short',
        });

        return (
          <button
            key={date}
            data-testid={`gym-planner-overview-${date}`}
            data-exercises={String(exerciseCount)}
            onClick={() => onSelectDay(date)}
            className={`flex items-center gap-3 px-4 py-3 text-left hover:bg-bg-elevated/40 transition-colors ${
              isSelected ? 'bg-[#A3E635]/5 border-l-2 border-l-[#A3E635]' : ''
            }`}
          >
            <div className="flex flex-col items-start w-24 flex-shrink-0">
              <span
                className={`font-oswald text-sm uppercase tracking-wider ${
                  isToday ? 'text-[#A3E635]' : 'text-white'
                }`}
              >
                {DOW_LABELS[i]} · {dayNum} {monthShort}
              </span>
              <span className="font-inter text-[11px] text-text-secondary capitalize">
                {weekdayLabel}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              {isRest ? (
                <p className="font-inter text-sm text-text-secondary">💤 Descanso</p>
              ) : exerciseCount > 0 ? (
                <>
                  <p className="font-inter text-sm text-white truncate">
                    {day?.sessionLabel?.trim() || 'Rutina sin nombre'}
                  </p>
                  <p className="font-inter text-xs text-text-secondary">
                    {exerciseCount} ejercicio{exerciseCount === 1 ? '' : 's'}
                  </p>
                </>
              ) : (
                <p className="font-inter text-xs text-text-secondary italic">Sin rutina</p>
              )}
            </div>
            {isRest ? (
              <span className="text-[11px] font-inter font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap text-text-secondary bg-bg-border/40 border-bg-border">
                Descanso
              </span>
            ) : (
              exerciseCount > 0 && (
                <span
                  className={`text-[11px] font-inter font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${
                    isPublished
                      ? 'text-[#22C55E] bg-[#22C55E]/10 border-[#22C55E]/30'
                      : 'text-[#F59E0B] bg-[#F59E0B]/10 border-[#F59E0B]/30'
                  }`}
                >
                  {isPublished ? 'Publicada' : 'Borrador'}
                </span>
              )
            )}
          </button>
        );
      })}
    </div>
  );
}

// ─── PLANIFICADOR: editor de día ──────────────────────────────────────────────

function PlannerDayPanel({
  date,
  day,
  userId,
  monitorId,
  onChange,
  onSaved,
}: {
  date: string;
  day: PlanDay;
  userId: string;
  monitorId: string;
  onChange: (updates: Partial<PlanDay>) => void;
  onSaved: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  // Estas cinco funciones escriben en cascada y NINGUNA miraba su resultado:
  // decían "✓ Publicado" pasara lo que pasara. Lo peor es el par
  // DELETE + INSERT de gym_routine_exercises: si el DELETE va y el INSERT no,
  // la rutina se queda VACÍA y al entrenador se le dice que está publicada.
  // El error no se autodestruye como savedMsg: se queda hasta el siguiente
  // intento, que para eso es un error.
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [catalogCategory, setCatalogCategory] = useState(CATEGORY_ORDER[0]);
  const [catalogExercises, setCatalogExercises] = useState<
    { id: string; name_es: string; name_en: string }[]
  >([]);
  const [templates, setTemplates] = useState<{ id: string; name: string }[]>([]);
  const [catalogoIlegible, setCatalogoIlegible] = useState(false);
  const [plantillasIlegibles, setPlantillasIlegibles] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [importingTemplate, setImportingTemplate] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);

  const dateLabel = new Date(`${date}T12:00:00`).toLocaleDateString('es-ES', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  useEffect(() => {
    supabase
      .from('gym_exercises')
      .select('id, name_es, name_en')
      .eq('active', true)
      .eq('category', catalogCategory)
      .order('name_es')
      .then(({ data, error }) => {
        // El catálogo vacío se lee como «no hay ejercicios de esta categoría».
        setCatalogoIlegible(!!error);
        if (!error) setCatalogExercises(data ?? []);
      });
  }, [catalogCategory]);

  useEffect(() => {
    supabase
      .from('gym_routine_templates')
      .select('id, name')
      .eq('monitor_id', monitorId)
      .order('name')
      .then(({ data, error }) => {
        setPlantillasIlegibles(!!error);
        if (!error) setTemplates(data ?? []);
      });
  }, [monitorId]);

  // Group by category for display: returns indices into day.exercises
  const grouped = useMemo(() => {
    return CATEGORY_ORDER.map((cat) => ({
      cat,
      indices: day.exercises
        .map((ex, i) => (ex.exerciseCategory === cat ? i : -1))
        .filter((i) => i >= 0),
    })).filter((g) => g.indices.length > 0);
  }, [day.exercises]);

  function addExercise(ex: { id: string; name_es: string; name_en: string }) {
    const newEx: PlanExercise = {
      exerciseId: ex.id,
      exerciseName: ex.name_en || ex.name_es,
      exerciseCategory: catalogCategory,
      blockLabel: '',
      sets: '3',
      reps: '10',
      weightKg: '',
      durationSeconds: '',
      distanceM: '',
      notes: '',
      requiresVideo: false,
    };
    onChange({ exercises: [...day.exercises, newEx] });
  }

  function removeExercise(absIdx: number) {
    onChange({ exercises: day.exercises.filter((_, i) => i !== absIdx) });
  }

  function updateExercise(absIdx: number, field: keyof PlanExercise, value: string | boolean) {
    onChange({
      exercises: day.exercises.map((e, i) => (i === absIdx ? { ...e, [field]: value } : e)),
    });
  }

  async function saveDay(publish: boolean) {
    setSaving(true);
    setErrorMsg(null);
    try {
      const newStatus = publish ? 'published' : 'draft';
      let routineId = day.routineId;

      if (routineId) {
        const { error: rutError } = await supabase
          .from('gym_routines')
          .update({
            routine_type: 'libre',
            session_label: day.sessionLabel || null,
            notes: day.notes || null,
            status: newStatus,
          })
          .eq('id', routineId);
        if (rutError) {
          setErrorMsg(`No se pudo guardar la rutina: ${rutError.message}`);
          return;
        }
      } else {
        const { data, error: insError } = await supabase
          .from('gym_routines')
          .insert({
            user_id: userId,
            monitor_id: monitorId,
            date,
            routine_type: 'libre',
            session_label: day.sessionLabel || null,
            notes: day.notes || null,
            status: newStatus,
          })
          .select('id')
          .single();
        // Sin esto, un fallo aquí dejaba routineId en undefined, el bloque de
        // ejercicios se saltaba entero y aun así se anunciaba "✓ Publicado".
        if (insError) {
          setErrorMsg(`No se pudo crear la rutina: ${insError.message}`);
          return;
        }
        routineId = (data as { id: string } | null)?.id;
        if (!routineId) {
          setErrorMsg('No se pudo crear la rutina: la base no devolvió su id.');
          return;
        }
      }

      // [228] Un solo viaje: `replace_routine_exercises` borra y vuelve a
      // insertar DENTRO de la misma sentencia. Antes eran un DELETE y un
      // INSERT sueltos y, si el segundo fallaba, la rutina se quedaba vacía —
      // medido contra producción: 3 ejercicios → 0. El #466 solo pudo avisar
      // de que había pasado; aquí ya no puede pasar, y por eso el aviso de
      // "se ha quedado SIN EJERCICIOS" desaparece con él.
      //
      // Se llama también con la lista vacía: vaciar la rutina es una acción
      // legítima y el DELETE de antes también lo hacía.
      if (routineId) {
        const { error: exsError } = await supabase.rpc('replace_routine_exercises', {
          p_routine_id: routineId,
          p_exercises: day.exercises.map((e) => ({
            exercise_id: e.exerciseId,
            block_label: e.blockLabel || null,
            sets: parseInt(e.sets) || null,
            reps: parseInt(e.reps) || null,
            weight_kg: parseFloat(e.weightKg) || null,
            time_seconds: parseInt(e.durationSeconds) || null,
            distance_meters: parseFloat(e.distanceM) || null,
            notes: e.notes || null,
            requires_video: e.requiresVideo,
          })),
        });
        if (exsError) {
          setErrorMsg(
            `No se pudieron guardar los ejercicios: ${exsError.message}. La rutina se ha quedado como estaba.`
          );
          return;
        }
      }

      onChange({ routineId: routineId ?? undefined, status: newStatus });
      setSavedMsg(publish ? '✓ Publicado — el alumno puede ver la rutina' : '✓ Borrador guardado');
      setTimeout(() => setSavedMsg(null), 3000);
      onSaved();
    } finally {
      setSaving(false);
    }
  }

  // FB-9 — marca el día como DESCANSO: rutina publicada con
  // routine_type='descanso' y 0 ejercicios. El alumno la ve y el día
  // cuenta como planificado (la RPC gym_players_unplanned_48h y
  // gym_routine_status_for_day lo tratan como 'rest', no como hueco).
  async function saveRest() {
    setSaving(true);
    setErrorMsg(null);
    try {
      // [245] Un solo viaje: `set_gym_rest_day` marca el día y borra sus
      // ejercicios DENTRO de la misma transacción. Antes eran un UPDATE y un
      // DELETE sueltos y, si el segundo fallaba, el día DECÍA descanso y
      // conservaba los ejercicios. El #470 solo pudo avisar de que había
      // pasado; la fila ya estaba mal y desde esta pantalla no había forma de
      // arreglarla. Aquí ya no puede pasar, y por eso ese aviso desaparece.
      //
      // Va por (alumno, fecha) y no por `routineId`: `gym_routines` tiene
      // UNIQUE (user_id, date), así que la RPC crea el día o lo convierte
      // según toque. Ya no importa si esta pantalla trae un id viejo — antes
      // ese caso chocaba con el único.
      const { data, error } = await supabase.rpc('set_gym_rest_day', {
        p_user_id: userId,
        p_date: date,
        p_monitor_id: monitorId,
        p_session_label: day.sessionLabel || null,
        p_notes: day.notes || null,
      });
      if (error) {
        setErrorMsg(`No se pudo marcar el día como descanso: ${error.message}`);
        return;
      }
      onChange({
        routineId: (data as string | null) ?? undefined,
        status: 'published',
        routineType: 'descanso',
        exercises: [],
      });
      setSavedMsg('✓ Día marcado como descanso');
      setTimeout(() => setSavedMsg(null), 3000);
      onSaved();
    } finally {
      setSaving(false);
    }
  }

  // FB-9 — quita el descanso: borra la rutina, el día vuelve a estar sin
  // planificar (la alerta de 48h volverá a contarlo).
  async function clearRest() {
    if (!day.routineId) return;
    setSaving(true);
    setErrorMsg(null);
    try {
      // [245] Los dos borrados, en la misma transacción. Antes, si el de la
      // rutina fallaba después del de los ejercicios, quedaba una rutina
      // publicada con CERO ejercicios; y para `gym_players_unplanned_48h` ese
      // día seguía contando como planificado, así que la alerta no lo cogía y
      // nadie se enteraba de que estaba vacío.
      //
      // La RPC además RECHAZA el borrado si el día ya no es un descanso. Eso
      // no arregla nada que estuviera roto: cierra la puerta que abre mover el
      // borrado aquí, porque esta pantalla podría pedirlo con estado viejo y
      // llevarse por delante una rutina de entrenamiento con sus ejercicios.
      const { error } = await supabase.rpc('clear_gym_rest_day', {
        p_routine_id: day.routineId,
      });
      if (error) {
        setErrorMsg(`No se pudo quitar el descanso: ${error.message}`);
        return;
      }
      onChange({
        routineId: undefined,
        status: 'draft',
        routineType: undefined,
        exercises: [],
      });
      setSavedMsg('✓ Descanso eliminado');
      setTimeout(() => setSavedMsg(null), 2000);
      onSaved();
    } finally {
      setSaving(false);
    }
  }

  async function importTemplate(templateId: string) {
    setImportingTemplate(true);
    setShowImportDropdown(false);
    setErrorMsg(null);
    // apply_gym_template es SECURITY DEFINER y lleva su propio gate: si el
    // entrenador no tiene al alumno asignado, devuelve error. Decir
    // "✓ Plantilla aplicada" sin mirarlo es anunciar un permiso que no hay.
    const { error } = await supabase.rpc('apply_gym_template', {
      p_template_id: templateId,
      p_user_id: userId,
      p_date: date,
      p_monitor_id: monitorId,
    });
    if (error) {
      setErrorMsg(`No se pudo aplicar la plantilla: ${error.message}`);
      setImportingTemplate(false);
      return;
    }
    setSavedMsg('✓ Plantilla aplicada');
    setTimeout(() => setSavedMsg(null), 2000);
    onSaved();
    setImportingTemplate(false);
  }

  async function handleSaveTemplate() {
    if (!templateName.trim() || !day.exercises.length) return;
    setSavingTemplate(true);
    setErrorMsg(null);
    const { data: tmplRaw, error: tmplError } = await supabase
      .from('gym_routine_templates')
      .insert({ monitor_id: monitorId, name: templateName.trim(), notes: day.notes || null })
      .select('id')
      .single();
    if (tmplError) {
      setErrorMsg(`No se pudo guardar la plantilla: ${tmplError.message}`);
      setSavingTemplate(false);
      return;
    }
    const tmpl = tmplRaw as { id: string } | null;
    if (tmpl) {
      const { error: exsError } = await supabase.from('gym_routine_template_exercises').insert(
        day.exercises.map((e, i) => ({
          template_id: tmpl.id,
          exercise_id: e.exerciseId,
          position: i,
          block_label: e.blockLabel || null,
          sets: parseInt(e.sets) || null,
          reps: parseInt(e.reps) || null,
          weight_kg: parseFloat(e.weightKg) || null,
          notes: e.notes || null,
          requires_video: e.requiresVideo,
        }))
      );
      // La plantilla ya existe: si sus ejercicios no entran, queda VACÍA y
      // aplicarla más adelante no traería nada. Hay que decirlo.
      if (exsError) {
        setErrorMsg(
          `La plantilla "${templateName.trim()}" se creó SIN EJERCICIOS: ${exsError.message}`
        );
        setSavingTemplate(false);
        return;
      }
      setTemplates((prev) => [...prev, { id: tmpl.id, name: templateName.trim() }]);
      setSavedMsg('✓ Plantilla guardada');
      setTimeout(() => setSavedMsg(null), 2000);
    }
    setTemplateName('');
    setShowTemplateModal(false);
    setSavingTemplate(false);
  }

  // FB-9: día de descanso = rutina publicada tipo 'descanso' sin ejercicios.
  const isRest = day.routineType === 'descanso' && day.exercises.length === 0;

  return (
    <div className="card space-y-4">
      {catalogoIlegible && (
        <AvisoFallo que="el catálogo de ejercicios" testId="gym-catalogo-ilegible" />
      )}
      {plantillasIlegibles && (
        <AvisoFallo que="tus plantillas de rutina" testId="gym-plantillas-ilegibles" />
      )}
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="font-oswald text-white text-lg capitalize">{dateLabel}</p>
        <div className="flex items-center gap-3">
          {!isRest && day.status === 'published' && (
            <span className="text-xs font-inter text-[#22C55E] bg-[#22C55E]/10 border border-[#22C55E]/30 px-2.5 py-1 rounded-full">
              Publicado
            </span>
          )}
          {savedMsg && <span className="text-xs font-inter text-[#A3E635]">{savedMsg}</span>}
          {errorMsg && (
            <span
              className="text-xs font-inter text-error border border-error/30 rounded px-2 py-1"
              data-testid="gym-dia-error"
            >
              {errorMsg}
            </span>
          )}
        </div>
      </div>

      {/* FB-9 — banner de día de descanso */}
      {isRest && (
        <div
          data-testid="gym-planner-rest-banner"
          className="flex items-center justify-between gap-3 bg-bg-elevated border border-bg-border rounded-xl px-4 py-3"
        >
          <span className="font-inter text-sm text-text-secondary">
            💤 <span className="text-white font-medium">Día de descanso</span> — planificado y
            visible para el alumno.
          </span>
          <button
            onClick={clearRest}
            disabled={saving}
            data-testid="gym-planner-clear-rest"
            className="btn-ghost text-xs whitespace-nowrap disabled:opacity-50"
          >
            Quitar descanso
          </button>
        </div>
      )}

      {/* Session label */}
      <div>
        <label className="text-xs text-text-secondary font-inter block mb-1">Etiqueta sesión</label>
        <input
          className="input h-9 text-sm w-28"
          placeholder="S4, Sem3..."
          value={day.sessionLabel}
          onChange={(e) => onChange({ sessionLabel: e.target.value })}
        />
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* LEFT: plan */}
        <div className="lg:col-span-3 space-y-3">
          <div>
            <label className="text-xs text-text-secondary font-inter block mb-1">
              📝 Nota para el alumno
            </label>
            <textarea
              className="input w-full resize-none text-sm"
              placeholder="Instrucciones, observaciones..."
              value={day.notes}
              onChange={(e) => onChange({ notes: e.target.value })}
              rows={2}
            />
          </div>

          {grouped.length === 0 && (
            <div className="text-center py-8 text-text-secondary font-inter text-sm border border-dashed border-bg-border rounded-xl">
              Añade ejercicios desde el catálogo →
            </div>
          )}

          {grouped.map(({ cat, indices }) => (
            <div key={cat} className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="font-oswald text-xs text-text-secondary tracking-widest uppercase">
                  {CATEGORY_LABELS[cat] ?? cat}
                </span>
                <div className="flex-1 h-px bg-bg-border" />
              </div>
              {indices.map((absIdx) => {
                const ex = day.exercises[absIdx];
                return (
                  <div
                    key={absIdx}
                    className="bg-bg-elevated border border-bg-border rounded-lg p-3 space-y-2"
                  >
                    <div className="flex items-center gap-2">
                      <select
                        value={ex.blockLabel}
                        onChange={(e) => updateExercise(absIdx, 'blockLabel', e.target.value)}
                        className={`text-xs font-inter font-semibold px-2 py-1 rounded border cursor-pointer bg-bg-surface outline-none ${
                          ex.blockLabel
                            ? (BLOCK_COLORS[ex.blockLabel] ??
                              'text-text-secondary border-bg-border')
                            : 'text-text-secondary border-bg-border'
                        }`}
                      >
                        <option value="">—</option>
                        {BLOCK_LABELS.map((b) => (
                          <option key={b} value={b}>
                            {b}
                          </option>
                        ))}
                      </select>
                      <span className="font-inter text-sm text-white flex-1 truncate">
                        {ex.exerciseName}
                      </span>
                      <button
                        onClick={() => updateExercise(absIdx, 'requiresVideo', !ex.requiresVideo)}
                        title="Requiere vídeo"
                        className={`p-1.5 rounded transition-colors ${
                          ex.requiresVideo
                            ? 'text-[#A3E635] bg-[#A3E635]/10'
                            : 'text-text-secondary hover:text-white'
                        }`}
                      >
                        <Video size={14} />
                      </button>
                      <button
                        onClick={() => removeExercise(absIdx)}
                        className="p-1.5 text-text-secondary hover:text-error transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <div className="flex gap-2">
                      {(
                        [
                          ['Series', 'sets'],
                          ['Reps', 'reps'],
                          ['Kg', 'weightKg'],
                        ] as const
                      ).map(([lbl, key]) => (
                        <div key={key} className="flex-1">
                          <label className="text-xs text-text-secondary font-inter">{lbl}</label>
                          <input
                            type="number"
                            className="input h-8 text-sm text-center"
                            placeholder="—"
                            value={ex[key]}
                            onChange={(e) => updateExercise(absIdx, key, e.target.value)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

          <div className="flex gap-2 pt-2">
            <button
              onClick={() => saveDay(false)}
              disabled={saving}
              className="flex-1 btn-ghost text-sm"
            >
              {saving ? 'Guardando...' : 'Guardar borrador'}
            </button>
            <button
              onClick={() => saveDay(true)}
              disabled={saving}
              className="flex-1 bg-[#A3E635] text-bg-primary font-oswald text-sm uppercase tracking-wide py-2.5 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {saving ? '...' : '✅ Publicar'}
            </button>
          </div>

          {/* FB-9 — marcar el día como descanso (solo si no hay ejercicios). */}
          {!isRest && (
            <button
              onClick={saveRest}
              disabled={saving || day.exercises.length > 0}
              data-testid="gym-planner-mark-rest"
              title={
                day.exercises.length > 0
                  ? 'Quita los ejercicios para marcar el día como descanso'
                  : 'Marca este día como descanso (planificación válida)'
              }
              className="w-full btn-ghost text-sm disabled:opacity-40"
            >
              💤 Marcar como descanso
            </button>
          )}
        </div>

        {/* RIGHT: catalog */}
        <div className="lg:col-span-2 space-y-3">
          <p className="font-oswald text-xs text-text-secondary tracking-widest">CATÁLOGO</p>

          <div className="flex flex-wrap gap-1">
            {CATEGORY_ORDER.map((cat) => (
              <button
                key={cat}
                onClick={() => setCatalogCategory(cat)}
                className={`text-xs font-inter px-2.5 py-1 rounded-full border transition-colors ${
                  catalogCategory === cat
                    ? 'bg-[#A3E635]/20 border-[#A3E635]/50 text-[#A3E635]'
                    : 'bg-bg-elevated border-bg-border text-text-secondary hover:text-white'
                }`}
              >
                {CATEGORY_LABELS[cat]}
              </button>
            ))}
          </div>

          <div
            className="bg-bg-elevated border border-bg-border rounded-xl overflow-hidden"
            style={{ maxHeight: '18rem', overflowY: 'auto' }}
          >
            {catalogExercises.length === 0 && (
              <div className="px-3 py-4 text-text-secondary font-inter text-sm text-center">
                Sin ejercicios
              </div>
            )}
            {catalogExercises.map((ex) => (
              <button
                key={ex.id}
                onClick={() => addExercise(ex)}
                className="w-full text-left px-3 py-2.5 text-sm font-inter text-white hover:bg-[#A3E635]/10 border-b border-bg-border last:border-0 transition-colors flex items-center gap-2"
              >
                <Plus size={13} className="text-[#A3E635] flex-shrink-0" />
                {ex.name_en || ex.name_es}
              </button>
            ))}
          </div>

          {/* Template actions */}
          <div className="space-y-2">
            <div className="relative">
              <button
                onClick={() => setShowImportDropdown(!showImportDropdown)}
                disabled={importingTemplate}
                className="w-full btn-ghost text-sm flex items-center gap-2 justify-center"
              >
                <FileDown size={14} />
                {importingTemplate ? 'Aplicando...' : 'Importar plantilla'}
              </button>
              {showImportDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-bg-surface border border-bg-border rounded-xl z-10 overflow-hidden shadow-lg">
                  {templates.length === 0 ? (
                    <div className="px-4 py-3 text-text-secondary font-inter text-sm text-center">
                      Sin plantillas guardadas
                    </div>
                  ) : (
                    templates.map((t) => (
                      <button
                        key={t.id}
                        onClick={() => importTemplate(t.id)}
                        className="w-full text-left px-4 py-2.5 text-sm font-inter text-white hover:bg-[#A3E635]/10 border-b border-bg-border last:border-0 transition-colors"
                      >
                        {t.name}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            <button
              onClick={() => setShowTemplateModal(true)}
              disabled={day.exercises.length === 0}
              className="w-full btn-ghost text-sm flex items-center gap-2 justify-center disabled:opacity-40"
            >
              <FileUp size={14} />
              Guardar como plantilla
            </button>
          </div>
        </div>
      </div>

      {/* Save template modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-bg-surface border border-bg-border rounded-2xl p-6 w-full max-w-sm space-y-4">
            <h3 className="font-oswald text-lg text-white">Guardar como plantilla</h3>
            <input
              autoFocus
              className="input w-full text-sm"
              placeholder="Nombre de la plantilla..."
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSaveTemplate()}
            />
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowTemplateModal(false);
                  setTemplateName('');
                }}
                className="flex-1 btn-ghost text-sm"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveTemplate}
                disabled={!templateName.trim() || savingTemplate}
                className="flex-1 bg-[#A3E635] text-bg-primary font-oswald text-sm uppercase py-2.5 rounded-lg hover:opacity-90 disabled:opacity-40"
              >
                {savingTemplate ? '...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── REVISIÓN ─────────────────────────────────────────────────────────────────

// §37 — sustituimos la sema antigua (ejecución del alumno: green/orange/red)
// por la sema de REVISIÓN del trainer:
//   - 'reviewed' (verde): el trainer corrigió todos los ejercicios completados
//     del día. Coincide con monitor_reviewed_at IS NOT NULL en todos los
//     gym_session_exercises completados (RPC `gym_routine_review_status_for_day`).
//   - 'pending' (rojo): la rutina fue ejecutada pero quedan ejercicios sin
//     corregir.
//   - 'no_session' (gris): rutina publicada que el alumno no ejecutó —
//     no se puede corregir aún.
//   - 'rest'/'none': descanso o sin rutina; no se pinta nada.
const REVIEW_DOT_COLORS: Record<string, string | null> = {
  reviewed: '#22C55E',
  pending: '#EF4444',
  no_session: '#6B7280',
  rest: null,
  none: null,
};
const REVIEW_DOT_LABELS: Record<string, string> = {
  reviewed: 'Rutina corregida',
  pending: 'Sin corregir',
  no_session: 'Rutina sin ejecutar',
  rest: 'Día de descanso',
  none: 'Sin rutina',
};

function RevisionTab({
  userId,
  monitorId,
  initialDate,
}: {
  userId: string;
  monitorId: string;
  initialDate: string | null;
}) {
  const today = new Date();
  const todayStr = toDateStr(today.getFullYear(), today.getMonth(), today.getDate());

  // Si llegamos vía deep-link `?date=YYYY-MM-DD` desde la vista semanal de
  // MonitorGymPage, calculamos el offset de semana para arrancar en ella y
  // preseleccionamos el día.
  const initialWeekOffset = initialDate ? computeWeekOffset(initialDate) : 0;
  const [weekOffset, setWeekOffset] = useState(initialWeekOffset);
  const [selectedDate, setSelectedDate] = useState<string | null>(initialDate ?? null);
  const [dayStatuses, setDayStatuses] = useState<Record<string, string>>({});
  const [semanaRevIlegible, setSemanaRevIlegible] = useState(false);
  const [loading, setLoading] = useState(true);

  const weekStart = getWeekStart(weekOffset);
  const weekDays = getWeekDays(weekStart);

  useEffect(() => {
    setLoading(true);
    Promise.all(
      weekDays.map(async (date) => {
        const { data, error } = await supabase.rpc('gym_routine_review_status_for_day', {
          p_user_id: userId,
          p_date: date,
        });
        // Igual que en el resumen: 'none' es una respuesta, y el fallo se
        // estaba contando como tal.
        if (error) return { date, status: 'desconocido' };
        return { date, status: (typeof data === 'string' ? data : 'none') as string };
      })
    ).then((results) => {
      const map: Record<string, string> = {};
      results.forEach(({ date, status }) => {
        map[date] = status;
      });
      setDayStatuses(map);
      setSemanaRevIlegible(results.some((r) => r.status === 'desconocido'));
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekOffset, userId]);

  return (
    <div className="space-y-4">
      {semanaRevIlegible && (
        <AvisoFallo
          que="el estado de revisión de la semana"
          testId="gym-revision-semana-ilegible"
        />
      )}
      <div className="flex items-center justify-between">
        <button
          onClick={() => {
            setWeekOffset((w) => w - 1);
            setSelectedDate(null);
          }}
          className="w-9 h-9 rounded-lg bg-bg-surface border border-bg-border flex items-center justify-center hover:border-[#A3E635]/40 transition-colors"
        >
          <ChevronLeft size={16} className="text-text-secondary" />
        </button>
        <span className="font-inter text-sm text-white capitalize">
          {weekRangeLabel(weekStart)}
        </span>
        <button
          onClick={() => {
            setWeekOffset((w) => w + 1);
            setSelectedDate(null);
          }}
          className="w-9 h-9 rounded-lg bg-bg-surface border border-bg-border flex items-center justify-center hover:border-[#A3E635]/40 transition-colors"
        >
          <ChevronRight size={16} className="text-text-secondary" />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-20">
          <div className="text-[#A3E635] font-oswald animate-pulse">Cargando...</div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-7 gap-1">
            {weekDays.map((date, i) => {
              const status = dayStatuses[date] ?? 'none';
              const dotColor = REVIEW_DOT_COLORS[status];
              const isToday = date === todayStr;
              const isSelected = selectedDate === date;
              return (
                <button
                  key={date}
                  data-testid={`gym-revision-day-${date}`}
                  data-status={status}
                  onClick={() => setSelectedDate(isSelected ? null : date)}
                  title={REVIEW_DOT_LABELS[status] ?? ''}
                  className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border transition-colors ${
                    isSelected
                      ? 'bg-[#A3E635]/10 border-[#A3E635]/50'
                      : isToday
                        ? 'border-[#A3E635]/40'
                        : 'border-bg-border hover:bg-bg-surface'
                  }`}
                >
                  <span className="font-inter text-xs text-text-secondary">{DOW_LABELS[i]}</span>
                  <span
                    className={`font-inter text-sm font-medium ${isToday ? 'text-[#A3E635]' : 'text-white'}`}
                  >
                    {new Date(`${date}T12:00:00`).getDate()}
                  </span>
                  <div
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ backgroundColor: dotColor ?? 'transparent' }}
                  />
                </button>
              );
            })}
          </div>

          {/* Leyenda de colores de revisión — coherente con MonitorGymPage */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] font-inter text-text-secondary px-1">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-[#22C55E]" /> Corregida
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-[#EF4444]" /> Sin corregir
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-[#6B7280]" /> Sin ejecutar
            </span>
          </div>
        </>
      )}

      {selectedDate && (
        <SessionReviewPanel
          key={selectedDate}
          date={selectedDate}
          userId={userId}
          monitorId={monitorId}
        />
      )}
    </div>
  );
}

// ─── REVISIÓN: detalle de sesión ──────────────────────────────────────────────

const FEEDBACK_STYLES: Record<string, string> = {
  pendiente: 'text-[#F59E0B] border-[#F59E0B]/40 bg-[#F59E0B]/10',
  correcto: 'text-[#22C55E] border-[#22C55E]/40 bg-[#22C55E]/10',
  mejorable: 'text-[#A3E635] border-[#A3E635]/40 bg-[#A3E635]/10',
  incorrecto: 'text-error border-error/40 bg-error/10',
};
const FEEDBACK_LABELS: Record<string, string> = {
  pendiente: 'Pendiente',
  correcto: '✓ Correcto',
  mejorable: '≈ Mejorable',
  incorrecto: '✗ Incorrecto',
};

function SessionReviewPanel({
  date,
  userId,
  monitorId,
}: {
  date: string;
  userId: string;
  monitorId: string;
}) {
  const [loading, setLoading] = useState(true);
  const [revisionIlegible, setRevisionIlegible] = useState(false);
  const [videosIlegibles, setVideosIlegibles] = useState(false);
  const [routine, setRoutine] = useState<RoutineBrief | null>(null);
  const [routineExercises, setRoutineExercises] = useState<RoutineExerciseEmbed[]>([]);
  const [session, setSession] = useState<SessionWithExercisesForReview | null>(null);
  const [sessionExMap, setSessionExMap] = useState<Record<string, SessionExerciseForReview>>({});
  const [videoMap, setVideoMap] = useState<Record<string, VideoRowBrief[]>>({});
  const [feedbackUpdating, setFeedbackUpdating] = useState<string | null>(null);
  const [feedbackLocal, setFeedbackLocal] = useState<Record<string, string>>({});
  // §39 (PR-G6) — estado optimista del flag "Revisada" + flag de update en curso.
  // Inicializado desde la session en el load(); toggle vía RPC `monitor_mark_session_reviewed`.
  const [reviewCompletedAt, setReviewCompletedAt] = useState<string | null>(null);
  const [reviewMarkUpdating, setReviewMarkUpdating] = useState(false);
  const [reviewMarkError, setReviewMarkError] = useState<string | null>(null);
  // §31 — comentario por vídeo (estado local mientras el monitor escribe).
  // El UPDATE solo se persiste al pulsar "Guardar feedback" (no en cada
  // tecla) para no machacar la fila en cada cambio.
  const [commentLocal, setCommentLocal] = useState<Record<string, string>>({});
  const [commentError, setCommentError] = useState<Record<string, string>>({});
  // Visor de vídeo: signed URL por videoId (genera bajo demanda).
  const [videoUrl, setVideoUrl] = useState<Record<string, string>>({});
  const [videoLoading, setVideoLoading] = useState<string | null>(null);
  const [videoOpen, setVideoOpen] = useState<Record<string, boolean>>({});
  // La presigned de R2 caduca en 1h; si el visor reutilizara una URL cacheada
  // pasada su TTL, R2 responde 403 y el <video> falla. Guardamos si ya hemos
  // reintentado (re-firmado) una vez tras un onError para no entrar en bucle:
  // se resetea en cada apertura de usuario, permitiendo 1 reintento por apertura.
  const videoRetriedRef = useRef<Record<string, boolean>>({});

  useEffect(() => {
    async function load() {
      setLoading(true);

      const { data: rtnRaw, error: errRtn } = await supabase
        .from('gym_routines')
        .select('id, notes, session_label, routine_type, status')
        .eq('user_id', userId)
        .eq('date', date)
        .maybeSingle();

      setRevisionIlegible(!!errRtn);
      if (errRtn) {
        setLoading(false);
        return;
      }

      const rtn = rtnRaw as unknown as RoutineBrief | null;
      setRoutine(rtn);
      if (!rtn) {
        setLoading(false);
        return;
      }

      const { data: rtnExs, error: errRtnExs } = await supabase
        .from('gym_routine_exercises')
        .select(
          'id, position, sets, reps, weight_kg, time_seconds, distance_meters, block_label, requires_video, notes, gym_exercises(name_es, name_en, category)'
        )
        .eq('routine_id', rtn.id)
        .order('position');
      if (errRtnExs) {
        setRevisionIlegible(true);
        setLoading(false);
        return;
      }
      setRoutineExercises((rtnExs ?? []) as unknown as RoutineExerciseEmbed[]);

      const { data: sessRaw, error: errSessRev } = await supabase
        .from('gym_sessions')
        .select(
          'id, started_at, finished_at, status, initial_fatigue, initial_motivation, final_fatigue, final_motivation, rating, comments, review_completed_at, review_completed_by, gym_session_exercises(id, routine_exercise_id, completed, actual_sets, actual_reps, actual_weight_kg, actual_time_seconds, actual_distance_meters, perceived_effort, notes, completed_at)'
        )
        .eq('routine_id', rtn.id)
        .eq('user_id', userId)
        .maybeSingle();

      // Sin la sesión, esta pantalla dice que el alumno no la ejecutó. Es la
      // pantalla desde la que el entrenador la marca como revisada.
      if (errSessRev) {
        setRevisionIlegible(true);
        setLoading(false);
        return;
      }

      const sess = sessRaw as unknown as SessionWithExercisesForReview | null;
      setSession(sess);
      setReviewCompletedAt(sess?.review_completed_at ?? null);
      setReviewMarkError(null);

      if (sess) {
        const seMap: Record<string, SessionExerciseForReview> = {};
        const seIds: string[] = [];
        (sess.gym_session_exercises ?? []).forEach((se) => {
          seMap[se.routine_exercise_id] = se;
          seIds.push(se.id);
        });
        setSessionExMap(seMap);

        if (seIds.length > 0) {
          const { data: vidsRaw, error: errVids } = await supabase
            .from('gym_videos')
            .select(
              'id, session_exercise_id, r2_key, feedback_status, feedback_comment, reviewed_at'
            )
            .in('session_exercise_id', seIds);

          // ⚠️ Un vídeo que no se lee se ve igual que un vídeo que el alumno
          //    no subió, y este panel es donde se le da el feedback. El
          //    ejercicio pedía vídeo: decir que no lo hay es acusarle.
          setVideosIlegibles(!!errVids);

          const vids = (vidsRaw ?? []) as VideoRowBrief[];
          const vMap: Record<string, VideoRowBrief[]> = {};
          vids.forEach((v) => {
            if (!vMap[v.session_exercise_id]) vMap[v.session_exercise_id] = [];
            vMap[v.session_exercise_id].push(v);
          });
          setVideoMap(vMap);

          const localFb: Record<string, string> = {};
          const localCmt: Record<string, string> = {};
          vids.forEach((v) => {
            localFb[v.id] = v.feedback_status;
            localCmt[v.id] = v.feedback_comment ?? '';
          });
          setFeedbackLocal(localFb);
          setCommentLocal(localCmt);
        }
      }
      setLoading(false);
    }
    load();
  }, [userId, date]);

  // Carga la presigned GET del vídeo bajo demanda (1ª vez que el monitor
  // abre el visor). El EF `gym-r2-download-url` ya autoriza por monitor
  // asignado o admin — admin web pasa el JWT del usuario activo en el
  // header Authorization que `supabase.functions.invoke` añade solo.
  //
  // §36 (PR-G4): la primera versión del botón (§31) fallaba con "No se
  // pudo cargar el vídeo" en producción porque el EF no respondía al
  // preflight CORS desde origen Vercel. Arreglado en la EF (CORS_HEADERS
  // en _shared/cors.ts). Aquí limpiamos el slot de error al iniciar la
  // descarga para que un reintento no muestre el error stale, y exponemos
  // el mensaje real del EF cuando viene en el body.
  // Firma SIEMPRE una presigned fresca y abre el visor. No reutiliza la URL
  // cacheada: la de R2 caduca en 1h y una URL vieja daría 403 al reproducir.
  async function signAndOpenVideo(videoId: string) {
    setVideoLoading(videoId);
    setCommentError((prev) => ({ ...prev, [videoId]: '' }));
    try {
      const { data, error } = await supabase.functions.invoke<{
        downloadUrl: string;
        expiresIn: number;
      }>('gym-r2-download-url', { body: { video_id: videoId } });
      if (error || !data?.downloadUrl) {
        const msg = error?.message ?? 'No se pudo cargar el vídeo';
        setCommentError((prev) => ({ ...prev, [videoId]: msg }));
        return;
      }
      setVideoUrl((prev) => ({ ...prev, [videoId]: data.downloadUrl }));
      setVideoOpen((prev) => ({ ...prev, [videoId]: true }));
    } finally {
      setVideoLoading(null);
    }
  }

  async function loadVideo(videoId: string) {
    // Cerrar si ya está abierto (no requiere red).
    if (videoOpen[videoId]) {
      setVideoOpen((prev) => ({ ...prev, [videoId]: false }));
      return;
    }
    // Abrir: apertura de usuario → permite 1 reintento y re-firma fresca.
    videoRetriedRef.current[videoId] = false;
    await signAndOpenVideo(videoId);
  }

  // onError del <video>: normalmente = presigned caducada (403). Reintenta UNA
  // vez re-firmando; si el reintento también falla, error genérico (sin prometer
  // que recargar lo arregle, que ya no hace falta).
  async function handleVideoError(videoId: string) {
    if (videoRetriedRef.current[videoId]) {
      setCommentError((prev) => ({
        ...prev,
        [videoId]: 'No se pudo reproducir el vídeo. Inténtalo de nuevo.',
      }));
      return;
    }
    videoRetriedRef.current[videoId] = true;
    // Descartar la URL (probablemente caducada) para forzar remount del <video>
    // y re-firmar una sola vez.
    setVideoUrl((prev) => {
      const next = { ...prev };
      delete next[videoId];
      return next;
    });
    await signAndOpenVideo(videoId);
  }

  // §39 (PR-G6) — toggle del flag "Revisión completada" vía RPC
  // SECURITY DEFINER `monitor_mark_session_reviewed`. Optimista en
  // UI (set inmediato del timestamp local) y rollback en caso de
  // error. La RPC ya valida monitor asignado / admin.
  async function toggleReviewCompleted() {
    if (!session) return;
    setReviewMarkError(null);
    const wasCompleted = reviewCompletedAt !== null;
    const next = wasCompleted ? null : new Date().toISOString();
    setReviewMarkUpdating(true);
    setReviewCompletedAt(next);
    const { error } = await supabase.rpc('monitor_mark_session_reviewed', {
      p_session_id: session.id,
      p_reviewed: !wasCompleted,
    });
    setReviewMarkUpdating(false);
    if (error) {
      // Rollback optimista.
      setReviewCompletedAt(wasCompleted ? new Date().toISOString() : null);
      setReviewMarkError(error.message);
    }
  }

  // §31 — guarda status + comentario juntos. Valida cliente
  // (mejorable/incorrecto exigen comentario no vacío); el trigger
  // BD `gym_video_feedback_requires_comment` (mig 092) hace de doble
  // red por si alguien llama al endpoint a pelo.
  async function saveFeedback(videoId: string, status: string) {
    const comment = (commentLocal[videoId] ?? '').trim();
    if ((status === 'mejorable' || status === 'incorrecto') && comment.length === 0) {
      setCommentError((prev) => ({
        ...prev,
        [videoId]: 'Comentario obligatorio cuando es mejorable o incorrecto',
      }));
      return;
    }
    setCommentError((prev) => ({ ...prev, [videoId]: '' }));
    setFeedbackUpdating(videoId);
    setFeedbackLocal((prev) => ({ ...prev, [videoId]: status }));
    const { error } = await supabase
      .from('gym_videos')
      .update({
        feedback_status: status as Database['public']['Enums']['gym_video_feedback_status'],
        feedback_comment: comment.length > 0 ? comment : null,
        reviewed_by: monitorId,
        reviewed_at: new Date().toISOString(),
      })
      .eq('id', videoId);
    setFeedbackUpdating(null);
    if (error) {
      setCommentError((prev) => ({ ...prev, [videoId]: error.message }));
    }
  }

  const formattedDate = new Date(`${date}T12:00:00`).toLocaleDateString('es-ES', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  const SESSION_STATUS_LABELS: Record<string, string> = {
    in_progress: 'En progreso',
    completed: 'Completado',
    partial: 'Parcial',
    abandoned: 'Abandonado',
  };
  const SESSION_STATUS_COLOR: Record<string, string> = {
    in_progress: '#F59E0B',
    completed: '#22C55E',
    partial: '#F59E0B',
    abandoned: '#EF4444',
  };

  const grouped = CATEGORY_ORDER.map((cat) => ({
    cat,
    items: routineExercises.filter((e) => e.gym_exercises?.category === cat),
  })).filter((g) => g.items.length > 0);

  const totalExercises = routineExercises.length;
  const doneCount = Object.values(sessionExMap).filter((se) => se.completed).length;
  const pct = totalExercises > 0 ? Math.round((doneCount / totalExercises) * 100) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="text-[#A3E635] font-oswald animate-pulse">Cargando...</div>
      </div>
    );
  }

  const isReviewed = reviewCompletedAt !== null;

  return (
    <div className="card border-[#A3E635]/20 bg-[#A3E635]/5 space-y-4">
      {revisionIlegible && (
        <AvisoFallo que="la sesión de este día" testId="gym-revision-ilegible" />
      )}
      {videosIlegibles && <AvisoFallo que="los vídeos del alumno" testId="gym-videos-ilegibles" />}
      {/* Header */}
      <div className="space-y-1">
        <div className="flex items-center gap-3 flex-wrap">
          {routine?.session_label && (
            <span className="font-oswald text-[#A3E635] text-xl">{routine.session_label}</span>
          )}
          <p className="font-oswald text-white text-base capitalize">{formattedDate}</p>
          {/* §39 (PR-G6) — badge "Revisada" si el monitor marcó el flag explícito */}
          {isReviewed && (
            <span
              data-testid="gym-review-completed-badge"
              className="inline-flex items-center gap-1 text-xs font-inter font-semibold px-2 py-0.5 rounded-full border border-[#22C55E]/40 bg-[#22C55E]/10 text-[#22C55E]"
            >
              <CheckCircle2 size={12} />
              Revisada
            </span>
          )}
        </div>
        {session ? (
          <div className="flex items-center gap-3 flex-wrap">
            <span
              className="font-inter text-sm"
              style={{ color: SESSION_STATUS_COLOR[session.status] ?? '#A0A0A0' }}
            >
              {SESSION_STATUS_LABELS[session.status] ?? session.status}
            </span>
            <span className="font-inter text-xs text-text-secondary">
              {pct}% completado ({doneCount}/{totalExercises})
            </span>
            <div className="flex-1 min-w-16 h-1.5 bg-bg-border rounded-full overflow-hidden">
              <div
                className="h-full bg-[#22C55E] rounded-full transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        ) : (
          routine && (
            <div className="flex items-center gap-2 text-text-secondary">
              <Clock size={14} />
              <span className="font-inter text-sm">El alumno aún no ha realizado esta sesión</span>
            </div>
          )
        )}
      </div>

      {!routine && (
        <p className="font-inter text-text-secondary text-sm">
          Sin rutina planificada para este día
        </p>
      )}

      {/* Trainer note */}
      {routine?.notes && (
        <div className="bg-bg-surface border border-bg-border rounded-xl p-3">
          <p className="font-inter text-xs text-text-secondary mb-1">📝 Nota</p>
          <p className="font-inter text-sm text-white italic">{routine.notes}</p>
        </div>
      )}

      {/* Wellness questionnaire */}
      {session && (session.initial_fatigue != null || session.initial_motivation != null) && (
        <div className="bg-bg-surface border border-bg-border rounded-xl p-3 space-y-2">
          <p className="font-inter text-xs text-text-secondary">❓ CUESTIONARIO WELLNESS</p>
          <div className="flex flex-wrap gap-4">
            {session.initial_fatigue != null && (
              <Metric label="Fatiga ini." value={`${session.initial_fatigue}/10`} />
            )}
            {session.initial_motivation != null && (
              <Metric label="Motivación ini." value={`${session.initial_motivation}/10`} />
            )}
            {session.final_fatigue != null && (
              <Metric label="Fatiga fin." value={`${session.final_fatigue}/10`} />
            )}
            {session.final_motivation != null && (
              <Metric label="Motivación fin." value={`${session.final_motivation}/10`} />
            )}
            {session.rating != null && (
              <Metric label="Valoración" value={'⭐'.repeat(session.rating)} />
            )}
          </div>
          {session.comments && (
            <p className="font-inter text-xs text-text-secondary italic border-l-2 border-bg-border pl-2">
              {session.comments}
            </p>
          )}
        </div>
      )}

      {/* §39 (PR-G6) — Botón explícito "Marcar revisión completada".
          Solo aparece si el alumno ha ejecutado la sesión (session != null).
          Toggle bidireccional; estado persistido en gym_sessions
          (review_completed_at). Cuando está activo, la rutina aparece
          como `reviewed` en gym_routine_review_status_for_day. */}
      {session && (
        <div className="flex flex-wrap items-center gap-3">
          <button
            data-testid="gym-mark-review-completed"
            onClick={toggleReviewCompleted}
            disabled={reviewMarkUpdating}
            className={
              isReviewed
                ? 'inline-flex items-center gap-2 text-sm font-inter font-semibold px-4 py-2 rounded-lg border border-[#22C55E]/40 bg-[#22C55E]/10 text-[#22C55E] hover:bg-[#22C55E]/15 transition-colors disabled:opacity-50'
                : 'inline-flex items-center gap-2 text-sm font-oswald tracking-wide uppercase px-4 py-2 rounded-lg bg-[#A3E635] text-bg-primary font-semibold hover:opacity-90 transition-opacity disabled:opacity-50'
            }
          >
            <CheckCircle2 size={16} />
            {reviewMarkUpdating
              ? 'Guardando...'
              : isReviewed
                ? 'Marcada como revisada · deshacer'
                : 'Marcar revisión completada'}
          </button>
          {reviewMarkError && (
            <p data-testid="gym-mark-review-error" className="font-inter text-xs text-error">
              {reviewMarkError}
            </p>
          )}
        </div>
      )}

      {/* Exercise sections */}
      {grouped.map(({ cat, items }) => (
        <div key={cat} className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="font-oswald text-sm text-white tracking-wide">
              {CATEGORY_LABELS[cat] ?? cat}
            </span>
            <div className="flex-1 h-px bg-bg-border" />
          </div>
          {items.map((re) => {
            const se = sessionExMap[re.id];
            const isDone = se?.completed === true;
            const seVideos = se ? (videoMap[se.id] ?? []) : [];
            return (
              <div
                key={re.id}
                className={`bg-bg-surface border rounded-xl p-3 space-y-2 ${isDone ? 'border-[#22C55E]/30' : 'border-bg-border'}`}
              >
                <div className="flex items-center gap-2">
                  {isDone ? (
                    <CheckCircle2 size={16} className="text-[#22C55E] flex-shrink-0" />
                  ) : (
                    <Circle size={16} className="text-text-secondary flex-shrink-0" />
                  )}
                  {re.block_label && (
                    <span
                      className={`text-xs font-inter font-semibold px-1.5 py-0.5 rounded border ${BLOCK_COLORS[re.block_label] ?? ''}`}
                    >
                      {re.block_label}
                    </span>
                  )}
                  <span
                    className={`font-inter text-sm flex-1 ${isDone ? 'text-white' : 'text-text-secondary'}`}
                  >
                    {re.gym_exercises?.name_en || re.gym_exercises?.name_es || '—'}
                  </span>
                  {re.requires_video && (
                    <span className="text-xs font-inter text-[#A3E635] bg-[#A3E635]/10 border border-[#A3E635]/30 px-1.5 py-0.5 rounded">
                      📹
                    </span>
                  )}
                </div>

                <div className="pl-6 flex flex-wrap gap-4 text-xs font-inter">
                  <span className="text-text-secondary">
                    Plan:{' '}
                    {buildPlannedSummary({
                      sets: re.sets,
                      reps: re.reps,
                      weightKg: re.weight_kg,
                      durationSeconds: re.time_seconds,
                      distanceM: re.distance_meters,
                    })}
                  </span>
                  {isDone && se && (
                    <span className="text-[#22C55E]">Real: {buildActualSummary(se)}</span>
                  )}
                  {se?.perceived_effort != null && (
                    <span className="text-text-secondary">Esfuerzo: {se.perceived_effort}/10</span>
                  )}
                </div>

                {/* Videos + feedback */}
                {seVideos.map((v) => {
                  const fbStatus = feedbackLocal[v.id] ?? v.feedback_status;
                  const cmt = commentLocal[v.id] ?? '';
                  const err = commentError[v.id];
                  const commentRequired = fbStatus === 'mejorable' || fbStatus === 'incorrecto';
                  const isOpen = !!videoOpen[v.id];
                  return (
                    <div key={v.id} className="pl-6 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <Video size={14} className="text-[#A3E635] flex-shrink-0" />
                        <span
                          className={`text-xs font-inter px-2 py-0.5 rounded border ${FEEDBACK_STYLES[fbStatus] ?? ''}`}
                        >
                          {FEEDBACK_LABELS[fbStatus] ?? fbStatus}
                        </span>
                        <button
                          data-testid={`gym-review-toggle-video-${v.id}`}
                          onClick={() => loadVideo(v.id)}
                          disabled={videoLoading === v.id}
                          className="text-xs font-inter px-2.5 py-1 rounded-full border border-[#A3E635]/40 text-[#A3E635] hover:bg-[#A3E635]/10 transition-colors disabled:opacity-50"
                        >
                          {videoLoading === v.id
                            ? 'Cargando...'
                            : isOpen
                              ? 'Ocultar vídeo'
                              : 'Ver vídeo del alumno'}
                        </button>
                        {(['correcto', 'mejorable', 'incorrecto'] as const).map((fb) => (
                          <button
                            key={fb}
                            data-testid={`gym-review-status-${v.id}-${fb}`}
                            onClick={() => setFeedbackLocal((prev) => ({ ...prev, [v.id]: fb }))}
                            disabled={feedbackUpdating === v.id}
                            className={`text-xs font-inter px-2.5 py-1 rounded-full border transition-colors ${
                              fbStatus === fb
                                ? FEEDBACK_STYLES[fb]
                                : 'border-bg-border text-text-secondary hover:text-white'
                            }`}
                          >
                            {FEEDBACK_LABELS[fb]}
                          </button>
                        ))}
                      </div>

                      {isOpen && videoUrl[v.id] && (
                        <video
                          data-testid={`gym-review-video-${v.id}`}
                          src={videoUrl[v.id]}
                          controls
                          onError={() => handleVideoError(v.id)}
                          className="w-full max-w-md rounded-lg border border-bg-border bg-black"
                        />
                      )}

                      <div className="space-y-1">
                        <label className="font-inter text-xs text-text-secondary">
                          Comentario{' '}
                          {commentRequired ? (
                            <span className="text-error">(obligatorio)</span>
                          ) : (
                            <span className="text-text-secondary">(opcional)</span>
                          )}
                        </label>
                        <textarea
                          data-testid={`gym-review-comment-${v.id}`}
                          value={cmt}
                          onChange={(e) =>
                            setCommentLocal((prev) => ({ ...prev, [v.id]: e.target.value }))
                          }
                          placeholder="Feedback al alumno..."
                          rows={2}
                          className={`w-full max-w-md bg-bg-elevated border rounded-lg px-3 py-2 text-sm font-inter text-white placeholder:text-text-secondary focus:outline-none focus:ring-1 ${
                            err
                              ? 'border-error ring-error'
                              : 'border-bg-border focus:ring-[#A3E635]/40 focus:border-[#A3E635]/40'
                          }`}
                        />
                        {err && (
                          <p
                            data-testid={`gym-review-comment-error-${v.id}`}
                            className="font-inter text-xs text-error"
                          >
                            {err}
                          </p>
                        )}
                      </div>

                      <div>
                        <button
                          data-testid={`gym-review-save-${v.id}`}
                          onClick={() => saveFeedback(v.id, fbStatus)}
                          disabled={feedbackUpdating === v.id || fbStatus === 'pendiente'}
                          className="text-xs font-inter px-3 py-1.5 rounded-md bg-[#A3E635] text-bg-primary font-semibold hover:opacity-90 disabled:opacity-50"
                        >
                          {feedbackUpdating === v.id ? 'Guardando...' : 'Guardar feedback'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="font-inter text-xs text-text-secondary">{label}</p>
      <p className="font-oswald text-sm text-white">{value}</p>
    </div>
  );
}

// Input compuesto: DayDetailPanel pasa el shape `PlannedExerciseDay`
// (camelCase desde el .map de transformación) y SessionReviewPanel construye
// el mismo shape inline desde `RoutineExerciseEmbed`. Mismo contrato — el
// helper acepta valores opcionales para tolerar nulls de la BD.
type PlannedSummaryInput = {
  sets: number | null | undefined;
  reps: number | null | undefined;
  weightKg: number | null | undefined;
  durationSeconds: number | null | undefined;
  distanceM: number | null | undefined;
};

function buildPlannedSummary(ex: PlannedSummaryInput): string {
  const parts: string[] = [];
  if (ex.sets && ex.reps) parts.push(`${ex.sets}×${ex.reps}`);
  else if (ex.sets) parts.push(`${ex.sets} series`);
  if (ex.weightKg) parts.push(`${ex.weightKg}kg`);
  if (ex.durationSeconds) parts.push(`${Math.round(ex.durationSeconds / 60)}min`);
  if (ex.distanceM) parts.push(`${ex.distanceM}m`);
  return parts.join(' · ') || '—';
}

// Shape mínimo que el helper lee — coincide con `SessionExerciseBrief` y
// `SessionExerciseForReview`.
type ActualSummaryInput = Pick<
  GymSessionExerciseRow,
  | 'completed'
  | 'actual_sets'
  | 'actual_reps'
  | 'actual_weight_kg'
  | 'actual_time_seconds'
  | 'actual_distance_meters'
>;

function buildActualSummary(c: ActualSummaryInput): string {
  if (!c.completed) return 'No completado';
  const parts: string[] = [];
  if (c.actual_sets) {
    const repsStr = Array.isArray(c.actual_reps) ? c.actual_reps.join('/') : (c.actual_reps ?? '');
    parts.push(`${c.actual_sets}×${repsStr || '?'}`);
  }
  if (c.actual_weight_kg) parts.push(`${c.actual_weight_kg}kg`);
  if (c.actual_time_seconds) parts.push(`${Math.round(c.actual_time_seconds / 60)}min`);
  if (c.actual_distance_meters) parts.push(`${c.actual_distance_meters}m`);
  return parts.join(' · ') || 'Completado';
}
